import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../config/api";
import FreshnessDot from "../components/FreshnessDot";
import { 
  TrendingUp, TrendingDown, RefreshCw, Clock, Newspaper,
  ExternalLink, Activity, BarChart3, Globe, DollarSign,
  Flame, AlertTriangle, Calendar, ChevronRight, Flag, Landmark,
  ChevronDown, ChevronUp, X, Layers, PieChart, FileText, 
  MessageSquare, Gauge, ArrowUpCircle, ArrowDownCircle, MinusCircle,
  CalendarDays, Building2, Droplets, Zap, Search, Info
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import HotInCongressCard from "../components/HotInCongressCard";
import AIAnalysisChat, { renderWithLinks } from "../components/AIAnalysisChat";
import OptionsScannerSection from "../components/OptionsScannerSection";
import { wmCellExtreme, wmCurrentBadge } from "../components/wmExtremes";

/* ⓘ popover with a row's data provenance (source, as-of date, release link,
   how the window cells are computed) — kept out of the table so it fits phones. */
function RowInfoPopover({ idx }) {
  const cellsNote = idx.unit === 'jobs' ? 'Cells = change in jobs/week vs 1 · 2 · 4 · 10 weeks earlier (5D · 10D · 20D · 50D). Green = hiring picking up.'
    : idx.unit === 'count' ? 'Cells = change in claims vs 1 · 2 · 4 · 10 · 20 · 40 · 52 · 156 · 260 weeks earlier (5D → 5Y). Red = more claims (worse).'
    : idx.unit === 'pp' ? 'Cells = change in percentage points (pp), not %. Positive = curve steepening.'
    : idx.symbol === 'TERMCBCCALLNS' ? 'Quarterly Fed series forward-filled to daily — short windows read 0.00 until the next quarterly print. Cells = % change of the rate.'
    : idx.symbol === 'OBMMIC30YF' ? 'Daily index, published with a 1-business-day lag. Cells = % change of the rate over the window.'
    : null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="ml-1.5 inline-flex items-center align-middle text-gray-500 hover:text-cyan-300"
                aria-label={`About ${idx.name}`} data-testid={`usret-info-${idx.symbol}`} onClick={e => e.stopPropagation()}>
          <Info className="w-3 h-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 max-w-[90vw] bg-[#0f0f1a] border-gray-700 text-gray-200 text-[11px] p-3 space-y-1.5 shadow-xl"
                      data-testid={`usret-info-content-${idx.symbol}`}>
        <div className="font-semibold text-white text-xs">{idx.name}</div>
        {idx.unit_label && <div><span className="text-gray-500">Unit:</span> {idx.unit_label}</div>}
        {idx.as_of && <div><span className="text-gray-500">{idx.unit === 'jobs' || idx.unit === 'count' ? 'Week ending:' : 'As of:'}</span> {idx.as_of}</div>}
        {idx.released && <div><span className="text-gray-500">Released:</span> {idx.released}</div>}
        {idx.headline && <div><span className="text-gray-500">Headline:</span> {idx.headline}</div>}
        {idx.four_week_avg != null && <div><span className="text-gray-500">4-week avg:</span> {idx.four_week_avg.toLocaleString()}</div>}
        {idx.continuing_claims != null && <div><span className="text-gray-500">Continuing claims:</span> {idx.continuing_claims.toLocaleString()} (week ending {idx.continuing_as_of})</div>}
        {idx.source && <div><span className="text-gray-500">Source:</span> {idx.source}</div>}
        {cellsNote && <div className="text-gray-400">{cellsNote}</div>}
        {idx.series && (
          <div className="pt-1 border-t border-gray-800">
            <div className="text-gray-500 mb-0.5">Last {idx.series.length} weeks</div>
            <div className="grid grid-cols-2 gap-x-3 font-mono text-[10px]">
              {idx.series.map(([d, v]) => (
                <div key={d} className="flex justify-between"><span className="text-gray-500">{d.slice(5)}</span><span className={idx.unit === 'jobs' ? (v >= 0 ? 'text-green-400' : 'text-red-400') : 'text-gray-200'}>{idx.unit === 'jobs' && v >= 0 ? '+' : ''}{Number(v).toLocaleString()}</span></div>
              ))}
            </div>
          </div>
        )}
        {idx.url && (
          <a href={idx.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-cyan-400 hover:text-cyan-200 underline decoration-dotted pt-1"
             data-testid={`usret-link-${idx.symbol}`}>Open release <ExternalLink className="w-3 h-3" /></a>
        )}
      </PopoverContent>
    </Popover>
  );
}

const MarketOverviewPage = () => {
  const [data, setData] = useState(null);
  const [news, setNews] = useState([]);
  const [politicalNews, setPoliticalNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [newsDropdownOpen, setNewsDropdownOpen] = useState(false);
  const [searchTicker, setSearchTicker] = useState("");
  const [tickerPaused, setTickerPaused] = useState(false);
  const [marketNews, setMarketNews] = useState([]);
  const [lastMarketDriver, setLastMarketDriver] = useState(null);
  const tickerRef = useRef(null);
  
  const [sectorPerformanceData, setSectorPerformanceData] = useState(null);
  const [sectorPerformanceLoading, setSectorPerformanceLoading] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState({});
  const [performanceTimeframe, setPerformanceTimeframe] = useState('day');
  
  const [economicIndicators, setEconomicIndicators] = useState(null);
  const [trumpPosts, setTrumpPosts] = useState(null);
  const [usFinancialNews, setUsFinancialNews] = useState(null);
  const [sentimentIndicator, setSentimentIndicator] = useState(null);
  const [earningsCalendar, setEarningsCalendar] = useState(null);
  const [earningsView, setEarningsView] = useState('this_week');
  const [marketMFI, setMarketMFI] = useState(null);
  const [marketRSI, setMarketRSI] = useState(null);
  const [paydayIndicator, setPaydayIndicator] = useState(null);
  const [moneyFlowIndicator, setMoneyFlowIndicator] = useState(null);
  const [globalIndices, setGlobalIndices] = useState(null);
  const [globalIndicesLastUpdate, setGlobalIndicesLastUpdate] = useState(null);
  const [treasuryData, setTreasuryData] = useState(null);
  const [usIndexReturns, setUsIndexReturns] = useState(null);
  const [cpi, setCpi] = useState(null);
  const [usRetSanity, setUsRetSanity] = useState(null);
  const [wmReturns, setWmReturns] = useState(null);
  const [usRetRefreshing, setUsRetRefreshing] = useState(false);
  const [techRead, setTechRead] = useState(null);
  const [techReadLoading, setTechReadLoading] = useState(false);
  const [techReadErr, setTechReadErr] = useState(null);
  const [techReadOpen, setTechReadOpen] = useState(false);

  const generateTechRead = (force = false) => {
    setTechReadLoading(true);
    setTechReadErr(null);
    axios.get(`${API}/sector-analysis/market-technical-read${force ? '?force=true' : ''}`, { timeout: 100000 })
      .then(res => { setTechRead(res.data); setTechReadOpen(true); })
      .catch(e => setTechReadErr(e?.response?.data?.detail || e.message))
      .finally(() => setTechReadLoading(false));
  };

  const loadIndexReturns = () => {
    setUsRetRefreshing(true);
    Promise.allSettled([
      axios.get(`${API}/market/us-index-returns`, { timeout: 45000 })
        .then(res => { if (res.data?.indices?.length) setUsIndexReturns(res.data); }),
      axios.get(`${API}/market/weighted-market-returns`, { timeout: 60000 })
        .then(res => { if (res.data?.rows?.length) setWmReturns(res.data); }),
      axios.get(`${API}/market/cpi`, { timeout: 45000 })
        .then(res => { if (res.data?.rows?.length) setCpi(res.data); }),
    ]).finally(() => {
      setUsRetRefreshing(false);
      axios.get(`${API}/market/us-index-returns/sanity`, { timeout: 45000 })
        .then(res => { if (res.data?.checks) setUsRetSanity(res.data); })
        .catch(() => setUsRetSanity(null));
    });
  };

  useEffect(() => { loadIndexReturns(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Retry helper for production cold-start resilience.
  // Tries up to 4 times with progressively longer timeouts. Serves cached
  // payload IMMEDIATELY so the page never goes blank, then re-fetches in
  // the background. On persistent failure auto-retries once 10s later.
  const fetchMarketData = useCallback(async () => {
    const LS_KEY = 'sp_market_overview_cache_v1';

    // 1) Serve any cached payload up to 24h old IMMEDIATELY
    let servedFromCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (cached?.data && Date.now() - cached.ts < 24 * 3600 * 1000) {
        setData({ ...cached.data, _stale: Date.now() - cached.ts > 3600 * 1000 });
        setLastUpdate(new Date(cached.ts));
        servedFromCache = true;
      }
    } catch {}

    if (!servedFromCache) setLoading(true);

    let lastErr = null;
    // 4 attempts; progressive timeouts to ride out 60s+ cold-start window
    const TIMEOUTS = [25000, 35000, 45000, 60000];
    for (let attempt = 0; attempt < TIMEOUTS.length; attempt++) {
      try {
        const overviewRes = await axios.get(
          `${API}/market/overview`,
          { timeout: TIMEOUTS[attempt] },
        );
        if (overviewRes.data) {
          setData(overviewRes.data);
          setLastUpdate(new Date());
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: overviewRes.data, ts: Date.now() })); } catch {}
          setLoading(false);
          return;
        }
      } catch (error) {
        lastErr = error;
        console.error(`Market overview attempt ${attempt + 1} failed:`, error.message);
        if (attempt < TIMEOUTS.length - 1) {
          await new Promise(r => setTimeout(r, (attempt + 1) * 2000));
        }
      }
    }
    // All attempts failed.
    if (servedFromCache) {
      // Already showing cached data — keep it, silently retry later
      console.warn('Market overview: live fetch failed, keeping cached payload');
      setTimeout(() => fetchMarketData(), 15000);
      setLoading(false);
      return;
    }
    // Last-resort cache lookup (any age)
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (cached?.data) {
        setData({ ...cached.data, _stale: true });
        setLastUpdate(new Date(cached.ts));
        setLoading(false);
        setTimeout(() => fetchMarketData(), 15000);
        return;
      }
    } catch {}
    console.error('Market overview: all attempts failed, no cache available', lastErr);
    setData(prev => prev || {
      indices: [], sectors: [], topGainers: [], topLosers: [],
      marketStatus: 'closed', marketSession: 'closed', marketTime: '',
      market_breadth: { advancing: 0, declining: 0 },
      _failed: true,
    });
    setLoading(false);
    // Auto-retry once more after 12s — covers most prod cold-start cases
    setTimeout(() => fetchMarketData(), 12000);
  }, []);

  // Separate news loading — runs all 5 fetches IN PARALLEL with Promise.allSettled
  // so one slow endpoint doesn't gate the others (was sequential, taking up to 40s
  // on cellular). Falls back to localStorage-cached market driver if all fail.
  const fetchAllNews = useCallback(async () => {
    const LS_KEY = 'sp_market_driver_cache_v1';
    try {
      const [newsRes, politicalRes, qqq, dia, iwm] = await Promise.allSettled([
        axios.get(`${API}/stock/news-aggregator/SPY`, { timeout: 25000 }),
        axios.get(`${API}/market/political-news`, { timeout: 25000 }),
        axios.get(`${API}/stock/news-aggregator/QQQ`, { timeout: 25000 }),
        axios.get(`${API}/stock/news-aggregator/DIA`, { timeout: 25000 }),
        axios.get(`${API}/stock/news-aggregator/IWM`, { timeout: 25000 }),
      ]);
      const pick = (r) => (r.status === 'fulfilled' ? r.value.data : { news: [] });
      const newsData = pick(newsRes);
      const politicalData = pick(politicalRes);
      const qqqData = pick(qqq);
      const diaData = pick(dia);
      const iwmData = pick(iwm);
      
      const twoHoursAgo = new Date();
      twoHoursAgo.setHours(twoHoursAgo.getHours() - 2);
      
      const filterRecentNews = (newsItems) => {
        return (newsItems || [])
          .filter(item => {
            if (!item.published) return true;
            try { return new Date(item.published) >= twoHoursAgo; } catch { return true; }
          })
          .sort((a, b) => {
            try { return new Date(b.published || 0) - new Date(a.published || 0); } catch { return 0; }
          });
      };
      
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      const filterMarketNews = (newsItems) => {
        return (newsItems || [])
          .filter(item => {
            if (!item.published) return true;
            try { return new Date(item.published) >= twentyFourHoursAgo; } catch { return true; }
          })
          .sort((a, b) => {
            try { return new Date(b.published || 0) - new Date(a.published || 0); } catch { return 0; }
          });
      };
      
      const allMarketNews = [
        ...(newsData?.news || []),
        ...(qqqData?.news || []),
        ...(diaData?.news || []),
        ...(iwmData?.news || [])
      ];
      
      const seenTitles = new Set();
      const uniqueMarketNews = allMarketNews.filter(item => {
        const titleKey = item.title?.toLowerCase().substring(0, 50);
        if (seenTitles.has(titleKey)) return false;
        seenTitles.add(titleKey);
        return true;
      });
      
      const filteredPoliticalNews = filterRecentNews(politicalData?.news);
      const filteredMarketNewsRecent = filterRecentNews(uniqueMarketNews);
      const filteredMarketNewsDaily = filterMarketNews(uniqueMarketNews);
      
      let driver = null;
      if (filteredPoliticalNews.length > 0) driver = filteredPoliticalNews[0];
      else if (filteredMarketNewsRecent.length > 0) driver = filteredMarketNewsRecent[0];
      else if (filteredMarketNewsDaily.length > 0) driver = filteredMarketNewsDaily[0];
      if (driver) {
        setLastMarketDriver(driver);
        try { localStorage.setItem(LS_KEY, JSON.stringify({ driver, ts: Date.now() })); } catch {}
      } else {
        // No driver from this run — fall back to cached driver (max 6h old)
        try {
          const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
          if (cached?.driver && Date.now() - cached.ts < 6 * 3600 * 1000) {
            setLastMarketDriver(cached.driver);
          }
        } catch {}
      }
      
      setNews(filterRecentNews(newsData?.news));
      setMarketNews(filterMarketNews(uniqueMarketNews));
      setPoliticalNews(filteredPoliticalNews);
    } catch (e) {
      console.warn("News loading failed:", e);
    }
  }, []);

  // Breaking-news resilience: retry shortly after load (covers cold-start
  // timeouts) and refresh every 3 minutes so the ticker stays current.
  useEffect(() => {
    const retry = setTimeout(() => fetchAllNews(), 20000);
    const id = setInterval(() => fetchAllNews(), 180000);
    return () => { clearTimeout(retry); clearInterval(id); };
  }, [fetchAllNews]);

  // Fetch sector/sub-sector performance data - this is the slowest endpoint (~30s)
  // Using extended timeout and graceful error handling to prevent page hanging
  const fetchSectorPerformance = useCallback(async () => {
    setSectorPerformanceLoading(true);
    try {
      const response = await axios.get(`${API}/market/sector-subsector-performance`, { timeout: 60000 });
      setSectorPerformanceData(response.data);
    } catch (error) {
      console.error("Error fetching sector performance:", error);
      // Don't block page - sector data is secondary
    } finally {
      setSectorPerformanceLoading(false);
    }
  }, []);

  // Fetch BLS Economic Indicators
  const fetchEconomicIndicators = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/economic-indicators`, { timeout: 15000 });
      setEconomicIndicators(response.data);
    } catch (error) {
      console.error("Error fetching economic indicators:", error);
    }
  }, []);

  // Fetch Trump Truth Social posts
  const fetchTrumpPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/trump-posts`, { timeout: 15000 });
      setTrumpPosts(response.data);
    } catch (error) {
      console.error("Error fetching Trump posts:", error);
    }
  }, []);

  // Fetch US Financial News
  const fetchUsFinancialNews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/us-financial-news`, { timeout: 15000 });
      setUsFinancialNews(response.data);
    } catch (error) {
      console.error("Error fetching US financial news:", error);
    }
  }, []);

  // Fetch Market Sentiment Indicator
  const fetchSentimentIndicator = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/sentiment-indicator`, { timeout: 15000 });
      setSentimentIndicator(response.data);
    } catch (error) {
      console.error("Error fetching sentiment indicator:", error);
    }
  }, []);

  // Fetch Earnings Calendar
  const fetchEarningsCalendar = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/earnings-calendar`, { timeout: 15000 });
      setEarningsCalendar(response.data);
    } catch (error) {
      console.error("Error fetching earnings calendar:", error);
    }
  }, []);

  // Fetch Market Money Flow Index
  const fetchMarketMFI = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/money-flow-index`, { timeout: 15000 });
      setMarketMFI(response.data);
    } catch (error) {
      console.error("Error fetching market MFI:", error);
    }
  }, []);

  // Fetch Market RSI Indicator
  const fetchMarketRSI = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/rsi-indicator`, { timeout: 15000 });
      setMarketRSI(response.data);
    } catch (error) {
      console.error("Error fetching market RSI:", error);
    }
  }, []);

  // Fetch Payday Anomaly Indicator
  const fetchPaydayIndicator = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/payday-indicator`, { timeout: 15000 });
      setPaydayIndicator(response.data);
    } catch (error) {
      console.error("Error fetching payday indicator:", error);
    }
  }, []);

  // Fetch Money In-Flow/Out-Flow Indicator
  const fetchMoneyFlowIndicator = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/money-flow-indicator`, { timeout: 15000 });
      setMoneyFlowIndicator(response.data);
    } catch (error) {
      console.error("Error fetching money flow indicator:", error);
    }
  }, []);

  // Fetch Global Market Indices (with retry)
  const fetchGlobalIndices = useCallback(async () => {
    const LS_KEY = 'sp_global_indices_cache_v1';
    // Try 3 attempts with backoff — server may be cold-starting on first hit
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const response = await axios.get(`${API}/market/global-indices`, { timeout: 25000 });
        if (response.data && response.data.indices?.length > 0) {
          setGlobalIndices(response.data);
          setGlobalIndicesLastUpdate(new Date());
          try { localStorage.setItem(LS_KEY, JSON.stringify({ data: response.data, ts: Date.now() })); } catch {}
          return;
        }
      } catch (error) {
        console.error(`Global indices attempt ${attempt + 1} failed:`, error.message);
        if (attempt < 2) await new Promise(r => setTimeout(r, (attempt + 1) * 2000));
      }
    }
    // Fall back to last cached payload (max 1h old) so user sees data instead of error
    try {
      const cached = JSON.parse(localStorage.getItem(LS_KEY) || 'null');
      if (cached?.data && Date.now() - cached.ts < 3600 * 1000) {
        setGlobalIndices({ ...cached.data, _stale: true });
        setGlobalIndicesLastUpdate(new Date(cached.ts));
        return;
      }
    } catch {}
    setGlobalIndices(prev => prev || { indices: [], asia: [], europe: [], africa: [], middle_east: [], americas: [], latin_america: [], error: true });
  }, []);

  // Fetch 10-Year Treasury Data
  const fetchTreasuryData = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/market/treasury`, { params: { timeframe: '1d' }, timeout: 15000 });
      setTreasuryData(response.data);
    } catch (error) {
      console.error("Error fetching treasury data:", error);
    }
  }, []);

  // Toggle expanded sector
  const toggleSectorExpanded = (sectorName) => {
    setExpandedSectors(prev => ({
      ...prev,
      [sectorName]: !prev[sectorName]
    }));
  };

  // Get performance value based on selected timeframe.  New windows count
  // exact trading days from the backend (5d/10d/20d/50d/100d/200d/1y/3y/5y).
  const getPerformanceValue = (item) => {
    switch (performanceTimeframe) {
      case '5d':   return item.ret_5d   ?? item.week_return ?? 0;
      case '10d':  return item.ret_10d  ?? 0;
      case '20d':  return item.ret_20d  ?? item.month_return ?? 0;
      case '50d':  return item.ret_50d  ?? item.three_month_return ?? 0;
      case '100d': return item.ret_100d ?? item.six_month_return ?? 0;
      case '200d': return item.ret_200d ?? 0;
      case '1y':   return item.ret_1y   ?? item.year_return ?? 0;
      case '3y':   return item.ret_3y   ?? 0;
      case '5y':   return item.ret_5y   ?? 0;
      default:     return item.day_return;
    }
  };

  // ==================== SERIALIZED DATA LOADING ====================
  // Backend has Semaphore(2) + ThreadPool(2) for yfinance — concurrent requests
  // cause cascading timeouts. ALL calls must be sequential, no parallelism.
  // No artificial delays — just one call at a time, each with error isolation.
  useEffect(() => {
    let cancelled = false;

    const safeCall = async (fn) => {
      if (cancelled) return;
      try { await fn(); } catch (e) { console.error(`${fn.name} failed:`, e); }
    };

    const loadAllData = async () => {
      // Phase 0: News fires immediately — breaking-news ticker + market
      // driver must never wait behind market-data fetches.
      const newsPromise = safeCall(fetchAllNews);

      // Phase 1: Core market data + global indices (high-priority hero sections)
      await Promise.all([safeCall(fetchMarketData), safeCall(fetchGlobalIndices)]);
      await newsPromise;

      // Phase 3: Lightweight indicator cards — independent, fetched in parallel
      await Promise.all([
        safeCall(fetchMarketMFI),
        safeCall(fetchMarketRSI),
        safeCall(fetchPaydayIndicator),
        safeCall(fetchMoneyFlowIndicator),
        safeCall(fetchTreasuryData),
        safeCall(fetchSentimentIndicator),
      ]);

      // Phase 4: Sector deep-dive
      await safeCall(fetchSectorPerformance);

      // Phase 5: Secondary data
      await Promise.all([safeCall(fetchEarningsCalendar), safeCall(fetchEconomicIndicators)]);

      // Phase 6: External news scrapes
      await Promise.all([safeCall(fetchTrumpPosts), safeCall(fetchUsFinancialNews)]);
    };

    loadAllData();

    // Refresh every 60s — all of these read the server's market_cache store
    // (recomputed by the backend worker once a minute), so this is cheap.
    const refreshInterval = setInterval(async () => {
      await safeCall(fetchMarketData);
      await Promise.all([
        safeCall(fetchMarketMFI), safeCall(fetchMarketRSI), safeCall(fetchMoneyFlowIndicator),
        safeCall(fetchPaydayIndicator), safeCall(fetchTreasuryData), safeCall(fetchSectorPerformance),
      ]);
      loadIndexReturns();
    }, 60000);

    return () => {
      cancelled = true;
      clearInterval(refreshInterval);
    };
  }, []); // eslint-disable-line

  // Format relative time (e.g., "2h ago", "1d ago")
  const formatRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return "";
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return "0.00";
    return Number(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatPercent = (pct) => {
    if (pct === null || pct === undefined) return "0.00%";
    const sign = pct >= 0 ? "+" : "";
    return `${sign}${Number(pct).toFixed(2)}%`;
  };

  const formatChange = (change) => {
    if (change === null || change === undefined) return "0.00";
    const sign = change >= 0 ? "+" : "";
    return `${sign}${Number(change).toFixed(2)}`;
  };

  const formatVolume = (vol) => {
    if (!vol) return "0";
    if (vol >= 1e9) return `${(vol / 1e9).toFixed(2)}B`;
    if (vol >= 1e6) return `${(vol / 1e6).toFixed(2)}M`;
    if (vol >= 1e3) return `${(vol / 1e3).toFixed(2)}K`;
    return vol.toString();
  };

  const getHeatmapColor = (change) => {
    if (change === null || change === undefined) return "#374151";
    if (change >= 3) return "#15803d";
    if (change >= 2) return "#16a34a";
    if (change >= 1) return "#22c55e";
    if (change >= 0.5) return "#4ade80";
    if (change >= 0) return "#86efac";
    if (change >= -0.5) return "#fca5a5";
    if (change >= -1) return "#f87171";
    if (change >= -2) return "#ef4444";
    if (change >= -3) return "#dc2626";
    return "#b91c1c";
  };

  const getChangeClass = (change) => {
    if (change === null || change === undefined) return "text-gray-400";
    return change >= 0 ? "text-green-500" : "text-red-500";
  };

  // Sector groups for the heatmap
  const sectorGroups = {
    "Technology": ["AAPL", "MSFT", "GOOGL", "NVDA", "AMD", "INTC", "META", "NFLX"],
    "Financial": ["JPM", "BAC", "WFC", "GS", "MS", "V", "MA", "PYPL"],
    "Healthcare": ["UNH", "JNJ", "PFE", "ABBV", "MRK", "LLY"],
    "Energy": ["XOM", "CVX", "COP", "SLB"],
    "Consumer": ["AMZN", "TSLA", "HD", "LOW", "TGT", "WMT", "COST"],
    "Communication": ["DIS", "CMCSA", "T", "VZ"],
    "Industrial": ["BA", "CAT", "RTX", "GE", "HON"],
    "Consumer Staples": ["KO", "PEP", "MCD", "SBUX"]
  };

  const getStockData = (ticker) => {
    return data?.all_stocks?.find(s => s.ticker === ticker) || null;
  };

  // Calculate heatmap cell size based on market cap (simplified)
  const getCellSize = (ticker) => {
    const megaCaps = ["AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META"];
    const largeCaps = ["TSLA", "JPM", "V", "MA", "UNH", "JNJ"];
    if (megaCaps.includes(ticker)) return "col-span-2 row-span-2";
    if (largeCaps.includes(ticker)) return "col-span-1 row-span-2";
    return "col-span-1 row-span-1";
  };

  // Combine political news + market news for the ticker (political first, then market)
  const allNewsForTicker = [...politicalNews, ...marketNews, ...news]
    .filter((item, idx, arr) => {
      // Remove duplicates
      const titleKey = item.title?.toLowerCase().substring(0, 50);
      return arr.findIndex(i => i.title?.toLowerCase().substring(0, 50) === titleKey) === idx;
    })
    .slice(0, 30); // More news items for faster ticker

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-gray-200 font-sans">
      {/* Top Header Bar - Finviz Style */}
      <header className="bg-[#1a1a2e] border-b border-gray-800">
        <div className="max-w-[1920px] mx-auto px-2">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-4">
              <a href="/" className="flex items-center gap-2" data-testid="logo-link" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_6ab71d84-1c0a-4dfc-96a7-091e2ccdbc04/artifacts/n9h4u4r1_Stock%20Pimp%20Logo.png" 
                  alt="Stock Pimp AI Logo" 
                  className="h-8 w-8 rounded"
                />
                <div className="text-lg font-bold text-white flex items-center gap-1">
                  <span className="text-emerald-400">STOCK</span>
                  <span className="text-white">PIMP</span>
                  <span className="text-xs text-gray-500 ml-1">AI</span>
                </div>
              </a>
              <nav className="hidden md:flex items-center gap-1 text-xs">
                <span className="px-2 py-1 text-white bg-gray-800 rounded">Market Overview</span>
                <a href="/stock" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Stock Analysis</a>
                <a href="/scanner" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Screener</a>
                <a href="/portfolio" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Portfolio</a>
                <a href="/options-generator" className="px-2 py-1 text-gray-400 hover:text-white hover:bg-gray-800 rounded" target="_blank" rel="noopener noreferrer">Options</a>
              </nav>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {/* Stock Search Bar */}
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const t = searchTicker.trim().toUpperCase();
                  if (t) window.open(`/stock?ticker=${t}`, '_blank', 'noopener,noreferrer');
                }}
                className="flex items-center gap-1.5"
                data-testid="overview-search-form"
              >
                <input
                  data-testid="overview-ticker-search-input"
                  type="text"
                  placeholder="Ticker..."
                  value={searchTicker}
                  onChange={(e) => setSearchTicker(e.target.value.toUpperCase())}
                  className="w-24 sm:w-36 h-7 text-xs bg-[#111] border border-gray-700 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none rounded px-2"
                />
                <button
                  data-testid="overview-search-button"
                  type="submit"
                  className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center justify-center"
                >
                  <Search className="w-3 h-3" />
                </button>
              </form>
              {/* News Dropdown Button */}
              <div className="relative">
                <button 
                  onClick={() => setNewsDropdownOpen(!newsDropdownOpen)}
                  className="flex items-center gap-1 text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-800"
                  data-testid="news-dropdown-toggle"
                >
                  <Newspaper className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">News</span>
                  <span className="bg-red-500 text-white text-[9px] px-1 rounded-full">{allNewsForTicker.length}</span>
                  {newsDropdownOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              </div>
              
              {lastUpdate && (
                <span className="text-gray-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {lastUpdate.toLocaleTimeString()}
                </span>
              )}
              <button 
                onClick={fetchMarketData}
                disabled={loading}
                className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* News Dropdown Panel */}
      {newsDropdownOpen && (
        <div className="fixed inset-0 z-50" onClick={() => setNewsDropdownOpen(false)}>
          <div 
            className="absolute top-12 right-4 w-[500px] max-w-[95vw] bg-[#111] border border-gray-700 rounded-lg shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="news-dropdown-panel"
          >
            <div className="flex items-center justify-between px-4 py-2 bg-gradient-to-r from-red-900/50 to-[#1a1a2e] border-b border-gray-700">
              <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-red-400" />
                <span className="font-semibold text-white">Latest News</span>
                <span className="text-[10px] text-gray-400">(Last 2 hours)</span>
              </div>
              <button onClick={() => setNewsDropdownOpen(false)} className="text-gray-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto divide-y divide-gray-800">
              {allNewsForTicker.length > 0 ? allNewsForTicker.map((item, idx) => (
                <a
                  key={idx}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-4 py-3 hover:bg-gray-800/50 transition-colors group"
                  data-testid={`dropdown-news-${idx}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-gray-200 group-hover:text-white leading-tight mb-1">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className={`px-1.5 py-0.5 rounded ${item.is_political ? 'bg-red-900/50 text-red-400' : 'bg-blue-900/50 text-blue-400'}`}>
                          {item.source || 'News'}
                        </span>
                        <span className="text-gray-500">{formatRelativeTime(item.published)}</span>
                      </div>
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-500 group-hover:text-blue-400 shrink-0 mt-1" />
                  </div>
                </a>
              )) : (
                <div className="px-4 py-8 text-center text-gray-500">
                  No recent news available
                </div>
              )}
            </div>
            <div className="px-4 py-2 bg-[#0a0a0a] border-t border-gray-700 text-[10px] text-gray-500 flex items-center justify-between">
              <span>Auto-refresh: 1 min</span>
              <span>Sources: CNBC, Politico, WSJ, Bloomberg, Yahoo Finance</span>
            </div>
          </div>
        </div>
      )}

      {/* Scrolling News Headline Ticker with Links */}
      <div className="bg-black border-b border-gray-800 overflow-hidden">
        <div 
          className="max-w-[1920px] mx-auto"
          onMouseEnter={() => setTickerPaused(true)}
          onMouseLeave={() => setTickerPaused(false)}
        >
          <div className="flex items-center">
            {/* Fixed Label */}
            <div className="bg-red-600 text-white px-3 py-1.5 font-bold text-xs flex items-center gap-1.5 shrink-0 z-10">
              <Landmark className="w-3.5 h-3.5" />
              BREAKING
            </div>
            
            {/* Scrolling Ticker */}
            <div className="flex-1 overflow-hidden">
              <div 
                ref={tickerRef}
                className={`flex items-center gap-6 py-1.5 ${tickerPaused ? '' : 'animate-news-ticker'}`}
                style={{
                  animation: tickerPaused ? 'none' : 'newsticker 15s linear infinite',
                }}
              >
                {allNewsForTicker.slice(0, 15).map((item, idx) => (
                  <a
                    key={idx}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 shrink-0 group"
                    data-testid={`ticker-news-${idx}`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${item.is_political ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <span className="text-xs text-gray-300 group-hover:text-white whitespace-nowrap max-w-[400px] truncate">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {formatRelativeTime(item.published)}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-blue-400" />
                  </a>
                ))}
                {/* Duplicate for seamless loop */}
                {allNewsForTicker.slice(0, 15).map((item, idx) => (
                  <a
                    key={`dup-${idx}`}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 shrink-0 group"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${item.is_political ? 'bg-red-500' : 'bg-blue-500'}`} />
                    <span className="text-xs text-gray-300 group-hover:text-white whitespace-nowrap max-w-[400px] truncate">
                      {item.title}
                    </span>
                    <span className="text-[10px] text-gray-500 shrink-0">
                      {formatRelativeTime(item.published)}
                    </span>
                    <ExternalLink className="w-3 h-3 text-gray-600 group-hover:text-blue-400" />
                  </a>
                ))}
              </div>
            </div>
            
            {/* Dropdown Toggle on Right */}
            <button 
              onClick={() => setNewsDropdownOpen(true)}
              className="bg-[#1a1a2e] text-gray-400 hover:text-white px-3 py-1.5 text-xs flex items-center gap-1 shrink-0 border-l border-gray-700"
            >
              More <ChevronDown className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>

      {/* Add ticker animation styles */}
      <style>{`
        @keyframes newsticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-news-ticker {
          animation: newsticker 60s linear infinite;
        }
        .animate-news-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>

      {/* Market Driver Label + Event - Under Breaking News Ticker */}
      {(() => {
        // Use cascading fallback: political -> recent market -> daily market -> cached
        const currentDriver = politicalNews[0] || marketNews[0] || lastMarketDriver;
        const driverSource = politicalNews[0] ? 'BREAKING' : 
                             marketNews[0] ? 'TODAY' : 
                             lastMarketDriver ? 'LAST KNOWN' : null;
        const isOlderNews = !politicalNews[0] && (marketNews[0] || lastMarketDriver);
        
        return (
          <div className="bg-black border-b border-red-900/50 py-2">
            <div className="max-w-[1920px] mx-auto px-4">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Landmark className="w-3 h-3 text-red-500" />
                <span className="text-[10px] font-bold text-red-400 uppercase tracking-widest">Market Driver</span>
                {driverSource && driverSource !== 'BREAKING' && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                    driverSource === 'TODAY' ? 'text-green-400 bg-green-900/30' :
                    'text-yellow-500 bg-yellow-900/30'
                  }`}>{driverSource}</span>
                )}
              </div>
              {currentDriver ? (
                <a 
                  href={currentDriver.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center group"
                >
                  <p className={`text-sm group-hover:text-red-300 transition-colors ${isOlderNews ? 'text-gray-300' : 'text-white'}`}>
                    {currentDriver.title}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-1">
                    <span className="text-[10px] text-red-400">{currentDriver.source}</span>
                    <span className="text-[10px] text-gray-500">{formatRelativeTime(currentDriver.published)}</span>
                    <ExternalLink className="w-3 h-3 text-gray-500 group-hover:text-red-400" />
                  </div>
                </a>
              ) : (
                <p className="text-sm text-gray-400 text-center">Loading market-driving news...</p>
              )}
            </div>
          </div>
        );
      })()}

      {loading && !data ? (
        <div className="max-w-[1920px] mx-auto">
          {/* Skeleton: index strip */}
          <div className="bg-[#111] border-b border-gray-800 px-2 py-3">
            <div className="flex gap-2 overflow-hidden">
              {[1,2,3,4,5,6,7].map((i) => (
                <div key={i} className="flex-1 min-w-[120px] bg-gray-900/50 rounded p-3 animate-pulse">
                  <div className="h-3 w-12 bg-gray-800 rounded mb-2" />
                  <div className="h-5 w-20 bg-gray-800 rounded mb-1" />
                  <div className="h-3 w-16 bg-gray-800 rounded" />
                </div>
              ))}
            </div>
          </div>
          {/* Skeleton: sectors */}
          <div className="bg-[#0d0d0d] border-b border-gray-800 px-3 py-3">
            <div className="h-4 w-32 bg-gray-800 rounded mb-3 animate-pulse" />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
              {Array(11).fill(0).map((_, i) => (
                <div key={i} className="h-16 bg-gray-900/60 rounded animate-pulse" />
              ))}
            </div>
          </div>
          {/* Skeleton: news / gainers / losers */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 p-3">
            {[1,2,3].map((c) => (
              <div key={c} className="space-y-2">
                <div className="h-4 w-24 bg-gray-800 rounded animate-pulse" />
                {Array(6).fill(0).map((_, i) => (
                  <div key={i} className="h-12 bg-gray-900/50 rounded animate-pulse" />
                ))}
              </div>
            ))}
          </div>
          <div className="text-center text-gray-600 text-xs pb-4">
            Loading market data… (typically &lt;2s, can take ~30s on a cold backend)
          </div>
        </div>
      ) : data ? (
        <main className="max-w-[1920px] mx-auto">
          {/* Soft amber pill instead of an alarming red banner when the
              overview endpoint is briefly slow. Auto-retry is already in
              flight; user sees existing data, just with a "refreshing" hint. */}
          {data._failed && (
            <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-center gap-3">
              <RefreshCw className="w-3 h-3 text-amber-400 animate-spin" />
              <span className="text-xs text-amber-300">
                Refreshing market data… (auto-retry every 12s)
              </span>
            </div>
          )}
      {/* Market AI Chat — moved directly under the news ticker */}
      <div className="max-w-[1920px] mx-auto px-2 my-3">
        <AIAnalysisChat
          mode="market"
          tickerName="US Market"
          extraContext={{
            indices: (data?.indices || []).slice(0, 6).map(i => ({
              symbol: i.symbol, name: i.name, price: i.price,
              change_pct: i.change_percent ?? i.changePercent,
            })),
            sectors_day: (sectorPerformanceData?.sectors || []).map(s => ({
              name: s.sector || s.name, etf: s.etf,
              day: s.day_return, week: s.week_return, month: s.month_return, year: s.year_return,
            })).slice(0, 11),
            sentiment: sentimentIndicator ? {
              vix:          sentimentIndicator.vix ?? sentimentIndicator.VIX,
              fear_greed:   sentimentIndicator.fear_greed ?? sentimentIndicator.value,
              label:        sentimentIndicator.classification || sentimentIndicator.label,
            } : null,
            macro: economicIndicators ? {
              us_10y:    economicIndicators.us_10y ?? economicIndicators.treasury_10y,
              dxy:       economicIndicators.dxy ?? economicIndicators.dollar_index,
              gold:      economicIndicators.gold,
              oil:       economicIndicators.oil ?? economicIndicators.crude,
            } : null,
            global: globalIndices ? Object.fromEntries(
              Object.entries(globalIndices).slice(0, 8)
            ) : null,
            mfi: marketMFI?.value ?? null,
            rsi: marketRSI?.value ?? null,
            payday_signal: paydayIndicator?.signal || null,
            weighted_market: wmReturns ? {
              windows: wmReturns.windows,
              rows: wmReturns.rows.map(r => ({
                key: r.key, name: r.name, current: r.current, note: r.note, values: r.values,
              })),
            } : null,
            us_index_returns: usIndexReturns ? {
              windows: usIndexReturns.windows,
              generated_at: usIndexReturns.generated_at,
              rows: usIndexReturns.indices.map(r => ({
                name: r.name, symbol: r.symbol, price: r.price, returns: r.returns,
                ...(r.unit ? { unit: r.unit } : {}), ...(r.as_of ? { as_of: r.as_of } : {}),
                ...(r.corrected_1d ? { corrected_1d: r.corrected_1d.source } : {}),
                ...(r.four_week_avg != null ? { four_week_avg: r.four_week_avg } : {}),
                ...(r.continuing_claims != null ? { continuing_claims: r.continuing_claims } : {}),
              })),
              cpi: cpi ? { as_of: cpi.as_of, rows: cpi.rows.map(r => ({
                name: r.name, mom: r.mom, mom_prev: r.mom_prev, ann_3m: r.ann_3m, ann_6m: r.ann_6m,
                yoy: r.yoy, yoy_prev: r.yoy_prev, yoy_12m_ago: r.yoy_12m_ago,
              })) } : null,
            } : null,
            subsectors: sectorPerformanceData?.sectors?.length ? sectorPerformanceData.sectors.map(s => ({
              sector: s.name, etf: s.etf, d: s.day_return,
              subs: (s.sub_sectors || []).map(x => ({ n: x.name, etf: x.etf, d: x.day_return })),
            })) : null,
          }}
        />
      </div>

          {/* Index Performance Strip */}
          <div className="bg-[#111] border-b border-gray-800 px-2 py-2">
            {/* Overall Market Cap Badge */}
            {(() => {
              const sp500 = data.indices?.find(i => i.name === 'S&P 500');
              const nasdaq = data.indices?.find(i => i.name === 'NASDAQ 100');
              const dow = data.indices?.find(i => i.name === 'DOW 30');
              const russell = data.indices?.find(i => i.name === 'Russell 2000');
              if (!sp500 || !russell) return null;
              // S&P 500 ≈ $50.5T, Russell 2000 ≈ $3T (no overlap = total US market ~$53.5T)
              const SP500_MCAP = 50.5e12;
              const RUSSELL_MCAP = 3e12;
              const totalMcap = SP500_MCAP + RUSSELL_MCAP;
              // Weighted average pct from all 4 indices (S&P=50.5T, NQ=28T, DOW=15T, RUS=3T)
              const weights = [
                { pct: sp500?.changePercent || 0, w: 50.5 },
                { pct: nasdaq?.changePercent || 0, w: 28 },
                { pct: dow?.changePercent || 0, w: 15 },
                { pct: russell?.changePercent || 0, w: 3 },
              ];
              const totalW = weights.reduce((s, x) => s + x.w, 0);
              const avgPct = weights.reduce((s, x) => s + x.pct * x.w, 0) / totalW;
              const dollarChange = totalMcap * (avgPct / 100);
              const isLoss = avgPct < 0;
              const absDollar = Math.abs(dollarChange);
              let dollarStr;
              if (absDollar >= 1e12) dollarStr = `$${(absDollar / 1e12).toFixed(2)}T`;
              else if (absDollar >= 1e9) dollarStr = `$${(absDollar / 1e9).toFixed(1)}B`;
              else dollarStr = `$${(absDollar / 1e6).toFixed(0)}M`;
              return (
                <div className={`mb-2 flex items-center justify-center gap-2 py-1.5 px-3 rounded-lg border ${
                  isLoss 
                    ? 'bg-red-950/40 border-red-800/50' 
                    : 'bg-green-950/40 border-green-800/50'
                }`} data-testid="market-cap-badge">
                  <span className="text-[10px] uppercase tracking-wider text-gray-400">US Market</span>
                  <FreshnessDot timestamp={lastUpdate} size="xs" showLabel={false} />
                  <span className={`text-sm font-bold ${isLoss ? 'text-red-400' : 'text-green-400'}`}>
                    {isLoss ? '-' : '+'}{dollarStr}
                  </span>
                  <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                    isLoss ? 'bg-red-900/60 text-red-300' : 'bg-green-900/60 text-green-300'
                  }`}>
                    {isLoss ? '' : '+'}{avgPct.toFixed(2)}%
                  </span>
                  <span className="text-[9px] text-gray-500">S&P + NQ + DOW + RUS</span>
                </div>
              );
            })()}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
              {(() => {
                const order = ['S&P 500', 'NASDAQ 100', 'NASDAQ COMP', 'DOW 30', 'Russell 2000', 'VIX', 'GLD', 'USO'];
                const sorted = [...(data.indices || [])].sort((a, b) => {
                  const ai = order.indexOf(a.name); const bi = order.indexOf(b.name);
                  return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
                });
                return sorted.map((index) => {
                const isLinked = index.name === 'GLD' || index.name === 'USO';
                return (
                <div 
                  key={index.ticker}
                  onClick={isLinked ? () => window.open(`/stock?ticker=${index.name}`, '_blank') : undefined}
                  className={`rounded p-2 text-center block ${
                    index.name === 'GLD' 
                      ? 'bg-gradient-to-br from-yellow-900/30 to-[#1a1a2e] border border-yellow-700/50 hover:border-yellow-500 hover:scale-105 transition-all cursor-pointer' 
                      : index.name === 'USO'
                      ? 'bg-gradient-to-br from-orange-900/30 to-[#1a1a2e] border border-orange-700/50 hover:border-orange-500 hover:scale-105 transition-all cursor-pointer'
                      : 'bg-[#1a1a2e]'
                  }`} 
                  data-testid={`index-${index.ticker}`}
                >
                  <div className={`text-[10px] uppercase tracking-wide ${
                    index.name === 'GLD' ? 'text-yellow-400 font-medium' 
                    : index.name === 'USO' ? 'text-orange-400 font-medium'
                    : 'text-gray-500'
                  }`}>{index.name}</div>
                  <div className="text-sm font-bold text-white">{formatPrice(index.price)}</div>
                  <div className={`text-xs font-medium ${getChangeClass(index.changePercent)}`}>
                    {formatChange(index.change)} ({formatPercent(index.changePercent)})
                  </div>
                  {index.preMarketChangePercent != null && index.preMarketChangePercent !== 0 && (
                    <div className={`text-[9px] mt-0.5 ${index.preMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid={`index-pm-${index.ticker}`}>
                      PM: {index.preMarketChangePercent > 0 ? '+' : ''}{index.preMarketChangePercent?.toFixed(2)}%
                    </div>
                  )}
                  {index.postMarketChangePercent != null && index.postMarketChangePercent !== 0 && (
                    <div className={`text-[9px] mt-0.5 ${index.postMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid={`index-ah-${index.ticker}`}>
                      AH: {index.postMarketChangePercent > 0 ? '+' : ''}{index.postMarketChangePercent?.toFixed(2)}%
                    </div>
                  )}
                </div>
                );
              })})()}
              
              {/* Market Money Flow Index Box - Clickable, opens in new tab */}
              {marketMFI && (
                <a 
                  href="/market-indicators" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-[#1a1a2e] to-[#0f172a] rounded p-2 text-center border border-purple-900/50 hover:border-purple-500 hover:scale-105 transition-all cursor-pointer" 
                  data-testid="market-mfi"
                >
                  <div className="text-[10px] text-purple-400 uppercase tracking-wide font-medium">Market MFI</div>
                  <div className="text-lg font-bold" style={{ color: marketMFI.signal_color }}>
                    {marketMFI.composite_mfi}
                  </div>
                  <div className="text-[10px] font-medium" style={{ color: marketMFI.signal_color }}>
                    {marketMFI.signal}
                  </div>
                  <div className="text-sm font-bold mt-0.5" style={{ color: marketMFI.today_flow_color }}>
                    {marketMFI.today_change >= 0 ? '+' : ''}{marketMFI.today_change}%
                  </div>
                </a>
              )}

              {/* 10-Year Treasury Yield Tile */}
              {treasuryData?.current && (
                <a
                  href="/treasury"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-[#1a1a1e] to-[#0f172a] rounded p-2 text-center border border-amber-900/50 hover:border-amber-500 hover:scale-105 transition-all cursor-pointer"
                  data-testid="treasury-10y"
                >
                  <div className="text-[10px] text-amber-400 uppercase tracking-wide font-medium">10Y Treasury</div>
                  <div className="text-lg font-bold text-amber-300">
                    {treasuryData.current.yield?.toFixed(2)}%
                  </div>
                  <div className={`text-[10px] font-medium ${
                    treasuryData.regression?.trend === 'RISING' ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {treasuryData.regression?.trend || '--'}
                  </div>
                  <div className={`text-sm font-bold mt-0.5 ${
                    treasuryData.current.change >= 0 ? 'text-red-400' : 'text-green-400'
                  }`}>
                    {treasuryData.current.change >= 0 ? '+' : ''}{treasuryData.current.change?.toFixed(3)}
                  </div>
                </a>
              )}

              {/* Market RSI Indicator Box - Clickable, opens in new tab */}
              {marketRSI && (
                <a 
                  href="/market-indicators" 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-gradient-to-br from-[#1a2e1a] to-[#0f172a] rounded p-2 text-center border border-cyan-900/50 hover:border-cyan-500 hover:scale-105 transition-all cursor-pointer" 
                  data-testid="market-rsi"
                >
                  <div className="text-[10px] text-cyan-400 uppercase tracking-wide font-medium">Market RSI</div>
                  <div className="text-lg font-bold" style={{ color: marketRSI.signal_color }}>
                    {marketRSI.composite_rsi}
                  </div>
                  <div className="text-[10px] font-medium" style={{ color: marketRSI.signal_color }}>
                    {marketRSI.signal}
                  </div>
                  <div className="text-sm font-bold mt-0.5" style={{ color: marketRSI.trend_color }}>
                    {marketRSI.today_change >= 0 ? '+' : ''}{marketRSI.today_change}%
                  </div>
                </a>
              )}

              {/* Payday Anomaly Indicator */}
              {paydayIndicator && (
                <a 
                  href="/payday-anomaly"
                  className="bg-gradient-to-br from-[#2e1a2e] to-[#0f172a] rounded p-2 text-center border border-amber-900/50 hover:border-amber-500 hover:scale-105 transition-all cursor-pointer relative group block" 
                  data-testid="payday-indicator"
                  title="Click for detailed view"
                 target="_blank" rel="noopener noreferrer">
                  <div className="text-[10px] text-amber-400 uppercase tracking-wide font-medium">Payday Risk</div>
                  <div className="text-lg font-bold" style={{ color: paydayIndicator.color }}>
                    {paydayIndicator.score > 0 ? '+' : ''}{paydayIndicator.score}
                  </div>
                  <div className="text-[10px] font-medium" style={{ color: paydayIndicator.color }}>
                    {paydayIndicator.signal}
                  </div>
                  <div className="text-[9px] text-gray-400 mt-0.5">
                    {paydayIndicator.nearest_payday?.days_away === 0 
                      ? 'PAYDAY TODAY' 
                      : `${paydayIndicator.nearest_payday?.days_away}d to payday`}
                    {paydayIndicator.nearest_payday?.holiday_adjusted && (
                      <span className="ml-1 text-amber-500">*</span>
                    )}
                  </div>
                  <div className="text-[8px] text-blue-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click for details →
                  </div>
                  
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none text-left">
                    <div className="text-xs font-semibold text-amber-400 mb-2">Payday Anomaly Indicator</div>
                    <div className="text-[10px] text-gray-300 mb-2">{paydayIndicator.description}</div>
                    
                    {/* Upcoming Holidays Section */}
                    {paydayIndicator.holiday_info?.upcoming_holidays?.length > 0 && (
                      <div className="text-[9px] bg-red-900/20 border border-red-900/30 rounded p-2 mb-2">
                        <div className="font-medium text-red-400 mb-1">🗓 Upcoming Holidays:</div>
                        {paydayIndicator.holiday_info.upcoming_holidays.slice(0, 2).map((h, i) => (
                          <div key={i} className="flex justify-between text-red-300/80">
                            <span>{h.name}</span>
                            <span>{h.days_away === 0 ? 'Today' : `${h.days_away}d`}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Upcoming Paydays */}
                    <div className="text-[9px] text-gray-500 border-t border-gray-700 pt-2 mt-2">
                      <div className="font-medium text-gray-400 mb-1">Upcoming Paydays:</div>
                      {paydayIndicator.upcoming_paydays?.slice(0, 3).map((pd, i) => (
                        <div key={i} className="flex justify-between items-center">
                          <span className="flex items-center gap-1">
                            {pd.type}
                            {pd.holiday_adjusted && (
                              <span className="text-amber-500 text-[8px]" title="Holiday adjusted">*</span>
                            )}
                          </span>
                          <span className="text-gray-400">{pd.days_away}d ({pd.coverage})</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Holiday adjustment note */}
                    {paydayIndicator.upcoming_paydays?.some(pd => pd.holiday_adjusted) && (
                      <div className="text-[8px] text-amber-500/70 mt-1">
                        * Adjusted for weekend/holiday
                      </div>
                    )}
                    
                    <div className="text-[8px] text-gray-600 mt-2 italic border-t border-gray-700 pt-2">
                      Pre-payday: Selling pressure | Post-payday: Buying pressure
                      <br/>
                      Holidays near paydays increase volatility
                    </div>
                  </div>
                </a>
              )}

              {/* Money In-Flow/Out-Flow Indicator */}
              {moneyFlowIndicator && (
                <a 
                  href="/money-flow"
                  className={`bg-gradient-to-br rounded p-2 text-center border hover:scale-105 transition-all cursor-pointer relative group ${
                    moneyFlowIndicator.flow_direction === 'IN' 
                      ? 'from-[#1a2e1a] to-[#0f172a] border-green-900/50 hover:border-green-500' 
                      : moneyFlowIndicator.flow_direction === 'OUT'
                      ? 'from-[#2e1a1a] to-[#0f172a] border-red-900/50 hover:border-red-500'
                      : 'from-[#1a1a2e] to-[#0f172a] border-gray-700 hover:border-gray-500'
                  }`}
                  data-testid="money-flow-indicator"
                 target="_blank" rel="noopener noreferrer">
                  <div className={`text-[10px] uppercase tracking-wide font-medium ${
                    moneyFlowIndicator.flow_direction === 'IN' ? 'text-green-400' :
                    moneyFlowIndicator.flow_direction === 'OUT' ? 'text-red-400' :
                    'text-gray-400'
                  }`}>
                    Money Flow
                  </div>
                  <div className="text-lg font-bold flex items-center justify-center gap-1" style={{ color: moneyFlowIndicator.color }}>
                    {moneyFlowIndicator.flow_direction === 'IN' && <ArrowUpCircle className="w-4 h-4" />}
                    {moneyFlowIndicator.flow_direction === 'OUT' && <ArrowDownCircle className="w-4 h-4" />}
                    {moneyFlowIndicator.flow_direction === 'NEUTRAL' && <MinusCircle className="w-4 h-4" />}
                    {moneyFlowIndicator.score > 0 ? '+' : ''}{moneyFlowIndicator.score}%
                  </div>
                  <div className="text-[10px] font-medium" style={{ color: moneyFlowIndicator.color }}>
                    {moneyFlowIndicator.signal}
                  </div>
                  <div className="text-[9px] text-gray-400 mt-0.5">
                    MFI: {moneyFlowIndicator.components?.mfi?.value} | RSI: {moneyFlowIndicator.components?.rsi?.value}
                  </div>
                  <div className="text-[8px] text-cyan-400 mt-1">
                    Click for details →
                  </div>
                  
                  {/* Tooltip on hover */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-72 bg-[#1a1a2e] border border-gray-700 rounded-lg p-3 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none text-left">
                    <div className={`text-xs font-semibold mb-2 ${
                      moneyFlowIndicator.flow_direction === 'IN' ? 'text-green-400' :
                      moneyFlowIndicator.flow_direction === 'OUT' ? 'text-red-400' :
                      'text-gray-400'
                    }`}>
                      Money In-Flow/Out-Flow Indicator
                    </div>
                    <div className="text-[10px] text-gray-300 mb-2">{moneyFlowIndicator.description}</div>
                    
                    {/* Component Breakdown */}
                    <div className="text-[9px] bg-gray-800/50 rounded p-2 mb-2 space-y-1">
                      <div className="font-medium text-gray-400 mb-1">Components:</div>
                      <div className="flex justify-between">
                        <span className="text-amber-400">Payday Phase:</span>
                        <span className="text-white">{moneyFlowIndicator.components?.payday?.phase}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-green-400">MFI (Money Flow):</span>
                        <span className="text-white">{moneyFlowIndicator.components?.mfi?.value}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-purple-400">RSI (Momentum):</span>
                        <span className="text-white">{moneyFlowIndicator.components?.rsi?.value}</span>
                      </div>
                    </div>
                    
                    <div className="text-[8px] text-gray-600 italic border-t border-gray-700 pt-2">
                      Combines payday cycles with technical indicators to predict institutional money flow
                    </div>
                  </div>
                </a>
              )}
            </div>
          </div>

          {/* US Index Returns Box (1D → 5Y) — always rendered so the refresh
              button is available after loading failures */}
          <div className="bg-[#0d0d0d] border-b border-gray-800 px-3 py-3" data-testid="us-index-returns-box">
              <div className="flex items-center gap-2 mb-2 px-1">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400 uppercase tracking-wider font-semibold">US Index Returns</span>
                <button
                  onClick={loadIndexReturns}
                  disabled={usRetRefreshing}
                  data-testid="us-index-returns-refresh-btn"
                  title="Refresh index + weighted market data"
                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-gray-700 text-[10px] text-gray-300 hover:text-white hover:border-emerald-500 disabled:opacity-50 transition-colors"
                >
                  <RefreshCw className={`w-3 h-3 ${usRetRefreshing ? 'animate-spin' : ''}`} />
                  {usRetRefreshing ? 'Refreshing…' : 'Refresh'}
                </button>
                <span className="text-[9px] text-gray-500">split-adjusted · sanity-checked · shaded MFI/RSI cells = level was overbought (≥70) / oversold (≤30) at that lookback</span>
                {usRetSanity && usRetSanity.mismatch_count === 0 && (usRetSanity.corrected_count || 0) === 0 && usRetSanity.checked_count > 0 && (
                  <span
                    data-testid="us-index-returns-sanity-ok"
                    title={`1D values cross-checked across 3 sources — Yahoo, MSN, CNBC (tolerance ±${usRetSanity.tolerance_pp}pp)`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-800 bg-emerald-950/40 text-[9px] text-emerald-300"
                  >
                    ✓ 1D verified · 3-source check ({usRetSanity.ok_count}/{usRetSanity.checked_count})
                  </span>
                )}
                {usRetSanity && (usRetSanity.corrected_count || 0) > 0 && (
                  <span
                    data-testid="us-index-returns-sanity-corrected"
                    title={(usRetSanity.corrected || []).map(m => `${m.name}: Yahoo ${m.yahoo == null ? '—' : `${m.yahoo > 0 ? '+' : ''}${m.yahoo}%`} vs MSN ${m.msn == null ? '—' : `${m.msn > 0 ? '+' : ''}${m.msn}%`} vs CNBC ${m.cnbc == null ? '—' : `${m.cnbc > 0 ? '+' : ''}${m.cnbc}%`} → using ${m.ours_1d > 0 ? '+' : ''}${m.ours_1d}% (2-source majority)`).join(' · ')}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-amber-700 bg-amber-950/40 text-[9px] text-amber-300 font-semibold"
                  >
                    ⚠ {usRetSanity.corrected_count} 1D value{usRetSanity.corrected_count > 1 ? 's' : ''} corrected via 2-source majority (MSN+CNBC): {(usRetSanity.corrected || []).map(m => m.name).join(', ')}
                  </span>
                )}
                {wmReturns?.stale_cells?.length > 0 && (
                  <span data-testid="us-index-returns-wm-stale"
                    title={`Yahoo history unavailable — ${wmReturns.stale_cells.length} indicator cell(s) shown from the last complete snapshot (${wmReturns.stale_as_of ? new Date(wmReturns.stale_as_of).toLocaleString() : 'earlier'}). Refreshes automatically when the feed recovers.`}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-amber-700 bg-amber-950/40 text-[9px] text-amber-300">
                    ⚠ MFI/RSI/VWAP/MACD/GICS: {wmReturns.stale_cells.length} cell{wmReturns.stale_cells.length > 1 ? 's' : ''} from last snapshot
                  </span>
                )}
                {wmReturns?.history_source === 'msn' && !(wmReturns?.stale_cells?.length > 0) && (
                  <span data-testid="us-index-returns-wm-msn" title="Yahoo history unavailable — indicator rows computed live from MSN Finance daily bars"
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-sky-800 bg-sky-950/40 text-[9px] text-sky-300">
                    indicators via MSN history
                  </span>
                )}

                {usRetSanity && usRetSanity.mismatch_count > 0 && (
                  <span
                    data-testid="us-index-returns-sanity-mismatch"
                    title={usRetSanity.mismatches.map(m => `${m.name}: Yahoo ${m.yahoo == null ? '—' : m.yahoo + '%'} vs MSN ${m.msn == null ? '—' : m.msn + '%'} vs CNBC ${m.cnbc == null ? '—' : m.cnbc + '%'} — no two sources agree, using MSN`).join(' · ')}
                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-rose-700 bg-rose-950/50 text-[9px] text-rose-300 font-semibold"
                  >
                    ⚠ {usRetSanity.mismatch_count} unresolved 1D value{usRetSanity.mismatch_count > 1 ? 's' : ''} (no 2 sources agree, using MSN): {usRetSanity.mismatches.map(m => m.name).join(', ')} — treat with caution
                  </span>
                )}
              </div>
              {!usIndexReturns?.indices?.length ? (
                <div className="text-xs text-gray-500 py-4 text-center" data-testid="us-index-returns-empty">
                  {usRetRefreshing
                    ? 'Loading index returns…'
                    : 'Index returns failed to load — hit Refresh to retry.'}
                </div>
              ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="us-index-returns-table">
                  <thead>
                    <tr className="text-gray-500 border-b border-gray-800">
                      <th className="text-left py-1.5 px-2 font-medium sticky left-0 z-10 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.9)] max-w-[58vw] sm:max-w-none overflow-hidden text-ellipsis bg-[#0d0d0d] group-hover:bg-[#161628]">Index</th>
                      {usIndexReturns.windows.map(w => (
                        <th key={w} className="text-center py-1.5 px-2 font-medium">{w}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Weighted market composite rows first — same values as
                        the Market Indicators page table (VIX shown below) */}
                    {wmReturns?.rows?.filter(r => r.key !== 'vix').map(row => (
                      <tr key={row.key} className="border-b border-gray-800/50 hover:bg-[#161628] bg-purple-950/10 group">
                        <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 z-10 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.9)] max-w-[58vw] sm:max-w-none overflow-hidden text-ellipsis bg-[#120d1c] group-hover:bg-[#161628]">
                          <span className="text-purple-300 font-semibold">{row.name}</span>
                          {row.current != null && (
                            <span className="text-gray-500 ml-1.5">{Number(row.current).toLocaleString()}</span>
                          )}
                          {(() => {
                            const b = wmCurrentBadge(row);
                            return b ? (
                              <span className={`ml-1.5 px-1.5 py-0.5 rounded border text-[8px] font-bold align-middle ${b.cls}`}
                                    data-testid={`wm-extreme-badge-${row.key}`}>{b.text}</span>
                            ) : null;
                          })()}
                          <span className="text-gray-600 ml-1.5 text-[9px]">{row.note}</span>
                        </td>
                        {usIndexReturns.windows.map(w => {
                          const v = row.values?.[w];
                          const ex = wmCellExtreme(row, w);
                          return (
                            <td key={w} className="text-center py-1.5 px-2" data-testid={`usret-wm-${row.key}-${w}`}
                                title={ex ? `${row.name} level ${w} ago: ${ex.lvl} (${ex.label})` : undefined}>
                              {v == null ? (
                                <span className="text-gray-600">—</span>
                              ) : (
                                <span className={`font-medium px-1.5 py-0.5 rounded ${v >= 0 ? 'text-green-400' : 'text-red-400'} ${ex ? ex.cls : ''}`}
                                      data-extreme={ex ? ex.label : undefined}>
                                  {v >= 0 ? '+' : ''}{Number(v).toFixed(2)}%
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {usIndexReturns.indices.map(idx => (
                      <tr key={idx.symbol} className="border-b border-gray-800/50 hover:bg-[#161628] group">
                        <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 z-10 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.9)] max-w-[58vw] sm:max-w-none overflow-hidden text-ellipsis bg-[#0d0d0d] group-hover:bg-[#161628]">
                          <span className="text-white font-semibold">{idx.name}</span>
                          <span className={`ml-1.5 ${idx.unit === 'jobs' ? (idx.price >= 0 ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold') : idx.unit === 'count' ? 'text-white font-semibold' : 'text-gray-500'}`}
                                title={idx.series ? `${idx.unit === 'count' ? 'Weekly claims (week ending → claims)' : 'Weekly 4-wk avg change (week ending → jobs/wk)'}:\n${idx.series.map(([d, v]) => `${d}: ${idx.unit === 'jobs' && v >= 0 ? '+' : ''}${Number(v).toLocaleString()}`).join('\n')}` : undefined}>
                            {idx.unit === 'jobs' && idx.price > 0 ? '+' : ''}{idx.price?.toLocaleString()}
                          </span>

                          {(idx.unit === 'pp' || idx.as_of || idx.unit === 'jobs' || idx.unit === 'count') && (
                            <RowInfoPopover idx={idx} />
                          )}
                        </td>
                        {usIndexReturns.windows.map(w => {
                          const v = idx.returns?.[w];
                          const corrected = w === '1D' && idx.corrected_1d;
                          return (
                            <td key={w} className="text-center py-1.5 px-2" data-testid={`usret-${idx.symbol}-${w}`}
                                title={corrected ? `Auto-corrected from ${idx.corrected_1d.source} (Yahoo gave ${idx.corrected_1d.was == null ? 'no value' : idx.corrected_1d.was + '%'})` : undefined}>
                              {v == null ? (
                                <span className="text-gray-600">—</span>
                              ) : (
                                <span className={`font-medium ${(idx.invert ? v <= 0 : v >= 0) ? 'text-green-400' : 'text-red-400'} ${corrected ? 'underline decoration-dotted decoration-amber-500 underline-offset-2' : ''}`}>
                                  {idx.unit === 'jobs' || idx.unit === 'count'
                                    ? `${v >= 0 ? '+' : ''}${Number(v).toLocaleString()}`
                                    : `${v >= 0 ? '+' : ''}${v.toFixed(2)}${idx.unit === 'pp' ? 'pp' : '%'}`}
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                    {cpi?.rows?.length > 0 && (() => {
                      const cpiCols = [["mom", "MoM"], ["mom_prev", "Prior MoM"], ["ann_3m", "3-mo ann."], ["ann_6m", "6-mo ann."],
                                       ["yoy", "YoY"], ["yoy_prev", "Prior YoY"], ["yoy_3m_ago", "YoY 3m ago"], ["yoy_6m_ago", "YoY 6m ago"],
                                       ["yoy_12m_ago", "YoY 1y ago"], ["ann_2y", "2-yr ann."]];
                      const monthLabel = new Date(cpi.as_of + "T12:00:00Z").toLocaleDateString([], { month: "short", year: "numeric" });
                      const trend = (r, k, prevK) => {
                        const v = r[k], pv = r[prevK];
                        if (v == null || pv == null) return null;
                        if (Math.abs(v - pv) < 0.005) return { arrow: "→", cls: "text-gray-500", title: "unchanged vs prior" };
                        return v > pv ? { arrow: "▲", cls: "text-red-400", title: `accelerating (was ${pv.toFixed(2)}%)` }
                                      : { arrow: "▼", cls: "text-green-400", title: `cooling (was ${pv.toFixed(2)}%)` };
                      };
                      return (
                        <>
                          <tr className="bg-amber-950/20 border-y border-amber-900/50" data-testid="cpi-section-header">
                            <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 z-10 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.9)] max-w-[58vw] sm:max-w-none overflow-hidden text-ellipsis bg-[#1a130d]">
                              <span className="text-amber-300 font-bold text-[10px] uppercase tracking-wider">CPI · Inflation</span>
                              <span className="text-gray-400 ml-2 text-[10px]">{monthLabel}</span>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <button type="button" className="ml-1.5 inline-flex items-center align-middle text-gray-500 hover:text-cyan-300" aria-label="About CPI" data-testid="cpi-info"><Info className="w-3 h-3" /></button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-72 max-w-[90vw] bg-[#0f0f1a] border-gray-700 text-gray-200 text-[11px] p-3 space-y-1.5 shadow-xl" data-testid="cpi-info-content">
                                  <div className="font-semibold text-white text-xs">CPI · Inflation</div>
                                  <div><span className="text-gray-500">Print:</span> {monthLabel} (as of {cpi.as_of})</div>
                                  <div><span className="text-gray-500">Source:</span> {cpi.source}</div>
                                  <div className="text-gray-400">Columns: MoM and prior MoM · 3- and 6-month annualized · YoY and prior YoY · YoY 3m / 6m / 1y ago · 2-yr annualized. ▲ red = accelerating vs prior, ▼ green = cooling. Headline & Core in bold; categories indented.</div>
                                  <div className="text-gray-500 pt-1 border-t border-gray-800">Series: {cpi.rows.map(r => `${r.name.split(' (')[0]} ${r.series_id}`).join(' · ')}</div>
                                </PopoverContent>
                              </Popover>
                            </td>
                            {cpiCols.map(([k, label]) => (
                              <td key={k} className="text-center py-1.5 px-2 text-[9px] uppercase tracking-wide text-amber-300/90 font-semibold whitespace-nowrap">{label}</td>
                            ))}
                          </tr>
                          {cpi.rows.map(r => (
                            <tr key={r.key} className={`border-b border-gray-800/50 hover:bg-[#161628] group ${r.major ? "" : "bg-black/20"}`} data-testid={`cpi-row-${r.key}`}>
                              <td className="py-1.5 px-2 whitespace-nowrap sticky left-0 z-10 shadow-[6px_0_8px_-6px_rgba(0,0,0,0.9)] max-w-[58vw] sm:max-w-none overflow-hidden text-ellipsis bg-[#0d0d0d] group-hover:bg-[#161628]">
                                <span className={`${r.major ? "text-white font-semibold" : "text-gray-300 pl-3"}`}>{r.name}</span>
                                <span className="text-gray-500 ml-1.5" title={`${r.series_id} · seasonally adjusted index`}>{r.index.toLocaleString()}</span>
                              </td>
                              {cpiCols.map(([k]) => {
                                const v = r[k];
                                const t = k === "mom" ? trend(r, "mom", "mom_prev") : k === "yoy" ? trend(r, "yoy", "yoy_prev") : null;
                                return (
                                  <td key={k} className="text-center py-1.5 px-2 whitespace-nowrap" data-testid={`cpi-${r.key}-${k}`}>
                                    {v == null ? <span className="text-gray-600">—</span> : (
                                      <span className={`font-medium ${k === "mom" || k === "yoy" ? "text-white" : "text-gray-300"}`}>
                                        {v >= 0 ? "+" : ""}{Number(v).toFixed(2)}%
                                        {t && <span className={`ml-1 text-[9px] ${t.cls}`} title={t.title}>{t.arrow}</span>}
                                      </span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </>
                      );
                    })()}
                  </tbody>
                </table>
              </div>
              )}
          </div>

          {/* AI Technical Read — on-demand interpretation of the US Index
              Returns technicals + GICS sector rotation (token-friendly:
              generated only on click, cached 1 min server-side) */}
          <div className="bg-[#0d0d0d] border-b border-gray-800 px-3 py-3" data-testid="market-technical-read-box">
            <div className="flex items-center gap-2 flex-wrap">
              <Landmark className="w-4 h-4 text-cyan-400" />
              <span className="text-xs text-cyan-400 uppercase tracking-wider font-semibold">AI Technical Read</span>
              <span className="text-[9px] text-gray-500">interprets the table above + GICS sector rotation · on-demand · cached 1 min</span>
              <button
                onClick={() => generateTechRead(!!techRead)}
                disabled={techReadLoading}
                data-testid="market-technical-read-btn"
                className="flex items-center gap-1 px-2 py-0.5 rounded border border-gray-700 text-[10px] text-gray-300 hover:text-white hover:border-cyan-500 disabled:opacity-50 transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${techReadLoading ? 'animate-spin' : ''}`} />
                {techReadLoading ? 'Analyzing…' : techRead ? 'Regenerate' : 'Generate AI Read'}
              </button>
              {techRead && (
                <button
                  onClick={() => setTechReadOpen(o => !o)}
                  data-testid="market-technical-read-toggle"
                  className="text-[10px] text-gray-400 hover:text-white px-2 py-0.5 rounded border border-gray-800"
                >
                  {techReadOpen ? 'Hide details' : 'Show details'}
                </button>
              )}
              {techRead?.generated_at && (() => {
                const gen = new Date(techRead.generated_at);
                const mins = Math.max(0, Math.round((Date.now() - gen.getTime()) / 60000));
                const rel = mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : `${(mins / 60).toFixed(1)}h ago`;
                return (
                  <span className="text-[9px] px-1.5 py-0.5 rounded border border-cyan-900/60 bg-cyan-950/30 text-cyan-300"
                        data-testid="market-technical-read-timestamp"
                        title={gen.toLocaleString()}>
                    Generated {gen.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} · {rel}
                  </span>
                );
              })()}
            </div>
            {techReadErr && (
              <p className="text-[11px] text-red-400 mt-2" data-testid="market-technical-read-error">{techReadErr}</p>
            )}
            {techRead && (
              <div className="mt-2">
                <div className="border border-cyan-900/70 bg-cyan-950/30 rounded-md p-2.5 text-xs leading-relaxed text-slate-100"
                     data-testid="market-technical-read-summary">
                  <span className="text-[9px] uppercase tracking-wider text-cyan-400 font-bold mr-2">Summary</span>
                  {renderWithLinks(techRead.summary)}
                </div>
                {techReadOpen && (
                  <div className="mt-2 border border-gray-800 bg-black/40 rounded-md p-3 text-[11px] leading-relaxed text-slate-200 whitespace-pre-wrap"
                       data-testid="market-technical-read-detail">
                    {renderWithLinks(techRead.detail)}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Global Market Indices Strip */}
          {!globalIndices && (
            <div className="bg-[#0d0d0d] border-b border-gray-800 px-3 py-3" data-testid="global-indices-skeleton">
              <div className="flex items-center gap-2 mb-2 px-1">
                <Globe className="w-4 h-4 text-blue-400/50" />
                <span className="text-xs text-blue-400/50 uppercase tracking-wider font-semibold">Global Markets</span>
                <button
                  onClick={fetchGlobalIndices}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-blue-400 bg-blue-900/30 hover:bg-blue-800/50 hover:text-blue-300 rounded-lg border border-blue-800/50 transition-colors"
                  data-testid="refresh-global-markets-skeleton"
                  title="Reload Global Markets"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Refresh
                </button>
                <span className="text-xs text-gray-600 ml-auto animate-pulse">Loading...</span>
              </div>
              {/* Skeleton Regional Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mb-2">
                {['europe', 'asia', 'middle_east', 'africa'].map(region => (
                  <div key={region} className="flex items-center justify-center gap-1.5 py-1 px-2 rounded border bg-gray-900/30 border-gray-800 animate-pulse">
                    <div className="h-2.5 w-12 bg-gray-700/50 rounded" />
                    <div className="h-3 w-14 bg-gray-700/50 rounded" />
                    <div className="h-4 w-12 bg-gray-700/50 rounded" />
                  </div>
                ))}
              </div>
              {/* Skeleton Index Cards */}
              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-1.5">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div key={i} className="rounded p-1.5 bg-[#111] border border-gray-800 animate-pulse">
                    <div className="h-2 w-full bg-gray-700/40 rounded mb-1" />
                    <div className="h-3 w-8 bg-gray-700/40 rounded mx-auto mb-1" />
                    <div className="h-2 w-10 bg-gray-700/40 rounded mx-auto" />
                  </div>
                ))}
              </div>
            </div>
          )}
          {globalIndices && (
            <div className="bg-[#0d0d0d] border-b border-gray-800 px-3 py-3" data-testid="global-indices-strip">
              <div className="flex items-center gap-2 mb-2 px-1">
                <Globe className="w-4 h-4 text-blue-400" />
                <span className="text-xs text-blue-400 uppercase tracking-wider font-semibold">Global Markets</span>
                <button
                  onClick={fetchGlobalIndices}
                  className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-blue-400 bg-blue-900/30 hover:bg-blue-800/50 hover:text-blue-300 rounded-lg border border-blue-800/50 transition-colors"
                  data-testid="refresh-global-markets"
                  title="Reload Global Markets"
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Refresh
                </button>
                <a href="/global-markets" className="text-xs text-blue-500 hover:text-blue-300 ml-auto font-medium" target="_blank" rel="noopener noreferrer">
                  Full View →
                </a>
              </div>
              {globalIndices.error && !globalIndices.indices?.length && (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Failed to load — click Refresh to retry
                </div>
              )}
              {/* Regional Market Cap Badges */}
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mb-3">
                {[
                  { key: 'europe', label: 'Europe', indices: globalIndices.europe, mcap: 12e12, color: 'blue' },
                  { key: 'asia', label: 'Asia', indices: globalIndices.asia, mcap: 19e12, color: 'purple' },
                  { key: 'middle_east', label: 'Middle East', indices: globalIndices.middle_east, mcap: 4.3e12, color: 'amber' },
                  { key: 'africa', label: 'Africa', indices: globalIndices.africa, mcap: 1.1e12, color: 'orange' },
                  { key: 'latin_america', label: 'LatAm', indices: globalIndices.latin_america, mcap: 2.8e12, color: 'yellow' },
                  { key: 'americas', label: 'Americas', indices: globalIndices.americas, mcap: 56e12, color: 'emerald' },
                ].filter(r => r.indices?.length > 0).map(region => {
                  const avgPct = region.indices.reduce((s, i) => s + (i.change_pct || 0), 0) / region.indices.length;
                  const dollarChange = region.mcap * (avgPct / 100);
                  const isLoss = avgPct < 0;
                  const absDollar = Math.abs(dollarChange);
                  const dollarStr = absDollar >= 1e12 ? `$${(absDollar / 1e12).toFixed(2)}T`
                    : absDollar >= 1e9 ? `$${(absDollar / 1e9).toFixed(1)}B`
                    : `$${(absDollar / 1e6).toFixed(0)}M`;
                  return (
                    <div key={region.key} className={`flex items-center justify-center gap-2 py-1.5 px-3 rounded border ${
                      isLoss ? 'bg-red-950/30 border-red-900/40' : 'bg-green-950/30 border-green-900/40'
                    }`} data-testid={`market-cap-badge-${region.key}`}>
                      <span className="text-xs uppercase tracking-wider text-gray-400 font-medium">{region.label}</span>
                      <span className={`text-sm font-bold ${isLoss ? 'text-red-400' : 'text-green-400'}`}>
                        {isLoss ? '-' : '+'}{dollarStr}
                      </span>
                      <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${
                        isLoss ? 'bg-red-900/50 text-red-300' : 'bg-green-900/50 text-green-300'
                      }`}>
                        {isLoss ? '' : '+'}{avgPct.toFixed(2)}%
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
                {[...(globalIndices.asia || []), ...(globalIndices.europe || []), ...(globalIndices.africa || []), ...(globalIndices.middle_east || []), ...(globalIndices.latin_america || []), ...(globalIndices.americas || [])].map((idx) => {
                  // Market hours in PST (UTC-8) for each exchange
                  const marketHoursPST = {
                    'Japan': '4:00p - 1:00a',
                    'South Korea': '5:00p - 12:30a',
                    'Hong Kong': '5:30p - 12:00a',
                    'China': '5:30p - 11:00p',
                    'Australia': '4:00p - 12:00a',
                    'India': '6:45p - 1:00a',
                    'Singapore': '5:00p - 1:00a',
                    'Taiwan': '5:00p - 9:30p',
                    'Thailand': '6:00p - 12:30a',
                    'Malaysia': '5:00p - 1:00a',
                    'Indonesia': '5:45p - 12:15a',
                    'Philippines': '5:30p - 9:30p',
                    'New Zealand': '2:00p - 9:45p',
                    'Europe': '11:00p - 9:30a',
                    'Germany': '1:00a - 9:30a',
                    'France': '1:00a - 9:30a',
                    'UK': '12:00a - 8:30a',
                    'Russia': '12:50a - 10:50a',
                    'Spain': '1:00a - 9:30a',
                    'Italy': '1:00a - 9:30a',
                    'Switzerland': '1:00a - 9:30a',
                    'Netherlands': '1:00a - 9:30a',
                    'Sweden': '1:00a - 9:30a',
                    'Poland': '1:00a - 9:00a',
                    'Belgium': '1:00a - 9:30a',
                    'Austria': '1:00a - 9:30a',
                    'Norway': '1:00a - 9:20a',
                    'Denmark': '1:00a - 9:00a',
                    'Finland': '2:00a - 10:30a',
                    'Portugal': '12:00a - 8:30a',
                    'Greece': '1:00a - 9:20a',
                    'Ireland': '12:00a - 8:30a',
                    'Vietnam': '5:00p - 10:00p',
                    'South Africa': '11:00p - 7:00a',
                    'Pan-Africa': '11:00p - 7:00a',
                    'Saudi Arabia': '12:00a - 7:00a',
                    'UAE': '12:00a - 6:00a',
                    'UAE (Dubai)': '12:00a - 6:00a',
                    'Qatar': '12:00a - 5:15a',
                    'Turkey': '1:00a - 9:00a',
                    'Israel': '12:00a - 9:25a',
                    'Argentina': '7:00a - 1:00p',
                    'Brazil': '7:00a - 1:00p',
                    'Chile': '6:30a - 1:00p',
                    'Colombia': '6:30a - 12:00p',
                    'Peru': '6:00a - 12:00p',
                    'Mexico': '5:30a - 12:00p',
                    'Regional': '7:00a - 1:00p',
                    'Canada': '6:30a - 1:00p',
                    'USA': '6:30a - 1:00p',
                  };
                  // Map index names to exchange codes for sector analysis
                  const exchangeCodeMap = {
                    'ADX': 'ADX',
                    'DFM': 'DFM',
                    'DAX': 'DAX',
                    'FTSE 100': 'FTSE',
                    'CAC 40': 'CAC',
                    'Euro Stoxx 50': 'STOXX',
                    'Nikkei 225': 'NIKKEI',
                    'KOSPI': 'KOSPI',
                    'Hang Seng': 'HSI',
                    'CSI 300': 'CSI',
                    'S&P/ASX 200': 'ASX',
                    'Tadawul': 'TASI',
                    'Qatar Exchange': 'QSE',
                    'BIST 100': 'BIST',
                    'TA-35': 'TA35',
                    'JSE': 'JSE',
                    'Africa Index': 'JSE',
                    'MERVAL': 'MERVAL',
                    'Bovespa': 'B3',
                    'IPSA': 'IPSA',
                    'COLCAP': 'BVC',
                    'S&P/BVL Peru': 'BVL',
                    'LatAm 40': 'LATAM',
                    'IPC': 'BMV',
                    'TSX Composite': 'TSX',
                    'MOEX': 'MOEX',
                    'IBEX 35': 'IBEX',
                    'FTSE MIB': 'MIB',
                    'SMI': 'SMI',
                    'AEX': 'AEX',
                    'OMX 30': 'OMXS',
                    'WIG 20': 'WIG',
                    'BEL 20': 'BEL',
                    'ATX': 'ATX',
                    'OBX': 'OBX',
                    'OMX C25': 'OMXC',
                    'OMX Helsinki': 'OMXH',
                    'PSI 20': 'PSI',
                    'BSE Sensex': 'BSE',
                    'STI': 'SGX',
                    'TWSE': 'TWSE',
                    'SET Index': 'SET',
                    'KLCI': 'KLSE',
                    'IDX Composite': 'IDX',
                    'PSEi': 'PSEI',
                    'NZX 50': 'NZX',
                    'VN-Index': 'HOSE',
                    'Athens GD': 'ATHEX',
                    'ISEQ': 'ISEQ',
                    'S&P 500': 'SPY',
                    'NASDAQ': 'QQQ',
                    'Dow Jones': 'DIA',
                  };
                  const exchangeCode = exchangeCodeMap[idx.name];
                  const sectorLink = exchangeCode ? `/exchange-sectors?exchange=${exchangeCode}` : '/global-markets';
                  
                  return (
                    <a
                      href={sectorLink}
                      key={idx.ticker}
                      className={`rounded-lg p-2.5 text-center transition-all hover:scale-105 ${
                        idx.direction === 'up' ? 'bg-green-900/15 border border-green-900/30' :
                        idx.direction === 'down' ? 'bg-red-900/15 border border-red-900/30' :
                        'bg-[#111] border border-gray-800'
                      }`}
                      data-testid={`overview-global-${idx.name.toLowerCase().replace(/[\s\/]/g, '-')}`}
                      target="_blank" rel="noopener noreferrer"
                      title={exchangeCode ? `View ${idx.name} sector breakdown` : idx.name}
                    >
                      <div className="text-xs text-gray-300 font-medium truncate">{idx.name}</div>
                      <div className="text-[11px] text-gray-500 truncate">{idx.country}</div>
                      <div className={`text-base font-bold ${
                        idx.change_pct > 0 ? 'text-green-400' : idx.change_pct < 0 ? 'text-red-400' : 'text-gray-400'
                      }`}>
                        {idx.change_pct > 0 ? '+' : ''}{idx.change_pct?.toFixed(1)}%
                      </div>
                      <div className={`text-[11px] ${idx.status === 'open' ? 'text-green-500' : 'text-gray-600'}`}>
                        {idx.status === 'open' ? 'Open' : 'Closed'} {idx.direction === 'up' ? '▲' : idx.direction === 'down' ? '▼' : '—'}
                      </div>
                      {idx.preMarketChangePercent != null && idx.preMarketChangePercent !== 0 && (
                        <div className={`text-[9px] ${idx.preMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          PM: {idx.preMarketChangePercent > 0 ? '+' : ''}{idx.preMarketChangePercent?.toFixed(2)}%
                        </div>
                      )}
                      {idx.postMarketChangePercent != null && idx.postMarketChangePercent !== 0 && (
                        <div className={`text-[9px] ${idx.postMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          AH: {idx.postMarketChangePercent > 0 ? '+' : ''}{idx.postMarketChangePercent?.toFixed(2)}%
                        </div>
                      )}
                      <div className="text-[11px] text-gray-500">{marketHoursPST[idx.country] || ''} <span className="text-gray-600">PST</span></div>
                      {idx.etf_ticker && (
                        <span
                          role="link"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(`/stock?ticker=${idx.etf_ticker}`, '_blank', 'noopener'); }}
                          className="inline-block cursor-pointer text-sm text-blue-400 font-mono font-bold mt-1 px-2 py-0.5 rounded bg-blue-900/25 hover:bg-blue-800/40 hover:text-blue-300 transition-colors"
                          data-testid={`overview-global-etf-${idx.etf_ticker}`}
                        >{idx.etf_ticker}</span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}

          {/* Market Status Bar */}
          <div className="bg-[#0d0d0d] border-b border-gray-800 px-2 py-1.5">
            <div className="flex items-center justify-center gap-4 text-xs">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full animate-pulse ${
                  data.marketStatus === 'open' ? 'bg-green-500' :
                  data.marketStatus === 'pre-market' ? 'bg-yellow-500' :
                  data.marketStatus === 'after-hours' ? 'bg-blue-500' :
                  'bg-gray-500'
                }`} />
                <span className={`font-semibold uppercase ${
                  data.marketStatus === 'open' ? 'text-green-400' :
                  data.marketStatus === 'pre-market' ? 'text-yellow-400' :
                  data.marketStatus === 'after-hours' ? 'text-blue-400' :
                  'text-gray-400'
                }`}>
                  {data.marketStatus === 'open' ? 'Market Open' :
                   data.marketStatus === 'pre-market' ? 'Pre-Market' :
                   data.marketStatus === 'after-hours' ? 'After Hours' :
                   'Market Closed'}
                </span>
              </div>
              <span className="text-gray-500">{data.marketTime}</span>
              {(data.marketStatus === 'pre-market' || data.marketStatus === 'after-hours') && (
                <span className="text-[10px] text-gray-500 bg-gray-800 px-2 py-0.5 rounded">
                  Extended Hours Trading Active
                </span>
              )}
            </div>
          </div>

          {/* Market Breadth Bar - Finviz Style */}
          <div className="bg-[#0d0d0d] border-b border-gray-800 px-2 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              {/* Advancing/Declining */}
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className="text-green-400">Advancing</span>
                    <span className="text-red-400">Declining</span>
                  </div>
                  <div className="h-2 bg-gray-800 rounded-full overflow-hidden flex">
                    <div 
                      className="bg-green-500 h-full transition-all"
                      style={{ width: `${(data.market_breadth?.advancing / Math.max(data.market_breadth?.advancing + data.market_breadth?.declining, 1)) * 100}%` }}
                    />
                    <div 
                      className="bg-red-500 h-full transition-all"
                      style={{ width: `${(data.market_breadth?.declining / Math.max(data.market_breadth?.advancing + data.market_breadth?.declining, 1)) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-[10px]">
                    <span className="text-green-400">{data.market_breadth?.advancing || 0}</span>
                    <span className="text-red-400">{data.market_breadth?.declining || 0}</span>
                  </div>
                </div>
              </div>

              {/* A/D Ratio */}
              <div className="text-center">
                <div className="text-gray-500 mb-1">A/D Ratio</div>
                <div className={`text-lg font-bold ${(data.market_breadth?.advance_decline_ratio || 0) >= 1 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.market_breadth?.advance_decline_ratio?.toFixed(2) || '0.00'}
                </div>
              </div>

              {/* Market Sentiment */}
              <div className="text-center">
                <div className="text-gray-500 mb-1">Market Sentiment</div>
                <div className={`text-lg font-bold ${(data.market_breadth?.avg_sector_change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(data.market_breadth?.avg_sector_change || 0) >= 0 ? 'BULLISH' : 'BEARISH'}
                </div>
              </div>

              {/* Sectors Performance */}
              <div className="text-center">
                <div className="text-gray-500 mb-1">Avg Sector</div>
                <div className={`text-lg font-bold ${(data.market_breadth?.avg_sector_change || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatPercent(data.market_breadth?.avg_sector_change)}
                </div>
              </div>
            </div>
          </div>

          {/* Market Sentiment Indicator - NEW */}
          {sentimentIndicator && (
            <div className="bg-[#0d0d0d] border-b border-gray-800 px-2 py-2" data-testid="sentiment-indicator">
              <div className="flex items-center justify-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <Gauge className="w-4 h-4 text-purple-400" />
                  <span className="text-gray-400 uppercase text-[10px] tracking-wider">Market Signal</span>
                </div>
                <div className="flex items-center gap-3">
                  {sentimentIndicator.action === 'BUY' ? (
                    <ArrowUpCircle className="w-6 h-6" style={{ color: sentimentIndicator.color }} />
                  ) : sentimentIndicator.action === 'SELL' ? (
                    <ArrowDownCircle className="w-6 h-6" style={{ color: sentimentIndicator.color }} />
                  ) : (
                    <MinusCircle className="w-6 h-6" style={{ color: sentimentIndicator.color }} />
                  )}
                  <span 
                    className="text-xl font-bold uppercase"
                    style={{ color: sentimentIndicator.color }}
                  >
                    {sentimentIndicator.overall_signal}
                  </span>
                  <span className="text-gray-400 text-sm">
                    ({sentimentIndicator.confidence} confidence)
                  </span>
                </div>
                <div className="hidden md:flex items-center gap-4 text-[10px] text-gray-500">
                  {sentimentIndicator.signals?.slice(0, 3).map((signal, idx) => (
                    <span key={idx} className="bg-gray-800 px-2 py-0.5 rounded">
                      {signal.name}: <span className={signal.score > 0 ? 'text-green-400' : signal.score < 0 ? 'text-red-400' : 'text-gray-400'}>
                        {signal.signal}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content Grid - Finviz Layout */}
          <div className="grid grid-cols-12 gap-0.5 p-1" data-testid="market-overview-content">
            {/* LEFT COLUMN - Signal Tables */}
            <div className="col-span-12 lg:col-span-3 space-y-0.5">
              {/* Top Gainers Table */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-green-500" />
                    <span className="text-xs font-semibold text-white">Top Gainers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-500">{data.marketSession === 'regular' ? 'Daily' : data.marketSession?.toUpperCase()}</span>
                    <button onClick={fetchMarketData} className="text-gray-500 hover:text-green-400 p-0.5 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-top-gainers" title="Reload">
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                <table className="w-full text-[11px]">
                  <thead className="bg-[#0d0d0d]">
                    <tr className="text-gray-500">
                      <th className="text-left px-2 py-1">Ticker</th>
                      <th className="text-right px-2 py-1">Last</th>
                      <th className="text-right px-2 py-1">Change</th>
                      <th className="text-right px-2 py-1">Volume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {data.top_gainers?.slice(0, 8).map((stock, idx) => (
                      <tr key={stock.ticker} className="hover:bg-gray-800/30" data-testid={`gainer-${stock.ticker}`}>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <a href={`/stock?ticker=${stock.ticker}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline font-medium">
                              {stock.ticker}
                            </a>
                            {stock.marketSession === 'pre-market' && (
                              <span className="text-[8px] text-yellow-400 bg-yellow-900/30 px-1 rounded">PM</span>
                            )}
                            {stock.marketSession === 'after-hours' && (
                              <span className="text-[8px] text-blue-400 bg-blue-900/30 px-1 rounded">AH</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right px-2 py-1">
                          <div className="text-gray-300">{formatPrice(stock.price)}</div>
                          {stock.afterHoursPrice && stock.marketSession !== 'after-hours' && (
                            <div className="text-[9px] text-blue-400">AH: {formatPrice(stock.afterHoursPrice)} <span className={stock.afterHoursChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.afterHoursChangePercent > 0 ? '+' : ''}{stock.afterHoursChangePercent?.toFixed(2)}%)</span></div>
                          )}
                          {stock.preMarketPrice && stock.marketSession !== 'pre-market' && (
                            <div className="text-[9px] text-yellow-400">PM: {formatPrice(stock.preMarketPrice)} <span className={stock.preMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.preMarketChangePercent > 0 ? '+' : ''}{stock.preMarketChangePercent?.toFixed(2)}%)</span></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-1 text-green-400 font-medium">
                          {formatPercent(stock.changePercent)}
                        </td>
                        <td className="text-right px-2 py-1 text-gray-400">{formatVolume(stock.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Top Losers Table */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                    <span className="text-xs font-semibold text-white">Top Losers</span>
                  </div>
                  <span className="text-[10px] text-gray-500">{data.marketSession === 'regular' ? 'Daily' : data.marketSession?.toUpperCase()}</span>
                </div>
                <table className="w-full text-[11px]">
                  <thead className="bg-[#0d0d0d]">
                    <tr className="text-gray-500">
                      <th className="text-left px-2 py-1">Ticker</th>
                      <th className="text-right px-2 py-1">Last</th>
                      <th className="text-right px-2 py-1">Change</th>
                      <th className="text-right px-2 py-1">Volume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {data.top_losers?.slice(0, 8).map((stock, idx) => (
                      <tr key={stock.ticker} className="hover:bg-gray-800/30" data-testid={`loser-${stock.ticker}`}>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <a href={`/stock?ticker=${stock.ticker}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline font-medium">
                              {stock.ticker}
                            </a>
                            {stock.marketSession === 'pre-market' && (
                              <span className="text-[8px] text-yellow-400 bg-yellow-900/30 px-1 rounded">PM</span>
                            )}
                            {stock.marketSession === 'after-hours' && (
                              <span className="text-[8px] text-blue-400 bg-blue-900/30 px-1 rounded">AH</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right px-2 py-1">
                          <div className="text-gray-300">{formatPrice(stock.price)}</div>
                          {stock.afterHoursPrice && stock.marketSession !== 'after-hours' && (
                            <div className="text-[9px] text-blue-400">AH: {formatPrice(stock.afterHoursPrice)} <span className={stock.afterHoursChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.afterHoursChangePercent > 0 ? '+' : ''}{stock.afterHoursChangePercent?.toFixed(2)}%)</span></div>
                          )}
                          {stock.preMarketPrice && stock.marketSession !== 'pre-market' && (
                            <div className="text-[9px] text-yellow-400">PM: {formatPrice(stock.preMarketPrice)} <span className={stock.preMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.preMarketChangePercent > 0 ? '+' : ''}{stock.preMarketChangePercent?.toFixed(2)}%)</span></div>
                          )}
                        </td>
                        <td className="text-right px-2 py-1 text-red-400 font-medium">
                          {formatPercent(stock.changePercent)}
                        </td>
                        <td className="text-right px-2 py-1 text-gray-400">{formatVolume(stock.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Most Active Table */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-yellow-500" />
                    <span className="text-xs font-semibold text-white">Most Active</span>
                  </div>
                  <span className="text-[10px] text-gray-500">Volume</span>
                </div>
                <table className="w-full text-[11px]">
                  <thead className="bg-[#0d0d0d]">
                    <tr className="text-gray-500">
                      <th className="text-left px-2 py-1">Ticker</th>
                      <th className="text-right px-2 py-1">Last</th>
                      <th className="text-right px-2 py-1">Change</th>
                      <th className="text-right px-2 py-1">Volume</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800/50">
                    {data.most_active?.slice(0, 6).map((stock, idx) => (
                      <tr key={stock.ticker} className="hover:bg-gray-800/30" data-testid={`active-${stock.ticker}`}>
                        <td className="px-2 py-1">
                          <div className="flex items-center gap-1">
                            <a href={`/stock?ticker=${stock.ticker}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline font-medium">
                              {stock.ticker}
                            </a>
                            {stock.marketSession === 'pre-market' && (
                              <span className="text-[8px] text-yellow-400 bg-yellow-900/30 px-1 rounded">PM</span>
                            )}
                            {stock.marketSession === 'after-hours' && (
                              <span className="text-[8px] text-blue-400 bg-blue-900/30 px-1 rounded">AH</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right px-2 py-1">
                          <div className="text-gray-300">{formatPrice(stock.price)}</div>
                          {stock.afterHoursPrice && stock.marketSession !== 'after-hours' && (
                            <div className="text-[9px] text-blue-400">AH: {formatPrice(stock.afterHoursPrice)} <span className={stock.afterHoursChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.afterHoursChangePercent > 0 ? '+' : ''}{stock.afterHoursChangePercent?.toFixed(2)}%)</span></div>
                          )}
                          {stock.preMarketPrice && stock.marketSession !== 'pre-market' && (
                            <div className="text-[9px] text-yellow-400">PM: {formatPrice(stock.preMarketPrice)} <span className={stock.preMarketChangePercent >= 0 ? 'text-green-400' : 'text-red-400'}>({stock.preMarketChangePercent > 0 ? '+' : ''}{stock.preMarketChangePercent?.toFixed(2)}%)</span></div>
                          )}
                        </td>
                        <td className={`text-right px-2 py-1 font-medium ${getChangeClass(stock.changePercent)}`}>
                          {formatPercent(stock.changePercent)}
                        </td>
                        <td className="text-right px-2 py-1 text-yellow-400 font-medium">{formatVolume(stock.volume)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* CENTER - S&P 500 Heatmap */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-[#111] border border-gray-800 rounded h-full">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <BarChart3 className="w-3.5 h-3.5 text-emerald-500" />
                    <span className="text-xs font-semibold text-white">S&P 500 Map</span>
                  </div>
                  <div className="flex items-center gap-3 text-[10px]">
                    <span className="text-green-400">▲ {data.market_breadth?.advancing || 0}</span>
                    <span className="text-red-400">▼ {data.market_breadth?.declining || 0}</span>
                    <button onClick={fetchMarketData} className="text-gray-500 hover:text-emerald-400 p-0.5 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-sp500-map" title="Reload">
                      <RefreshCw className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                
                {/* Treemap-style Heatmap */}
                <div className="p-2" data-testid="stock-heatmap">
                  {Object.entries(sectorGroups).map(([sector, tickers]) => (
                    <div key={sector} className="mb-2">
                      <div className="text-[10px] text-gray-500 uppercase tracking-wide mb-1 px-1">{sector}</div>
                      <div className="flex flex-wrap gap-0.5">
                        {tickers.map((ticker) => {
                          const stock = getStockData(ticker);
                          if (!stock) return null;
                          const isMegaCap = ["AAPL", "MSFT", "GOOGL", "AMZN", "NVDA", "META"].includes(ticker);
                          const isLargeCap = ["TSLA", "JPM", "V", "MA", "UNH", "JNJ", "AMD"].includes(ticker);
                          
                          return (
                            <a
                              key={ticker}
                              href={`/stock?ticker=${ticker}`}
                              className="rounded transition-all hover:opacity-80 hover:scale-105 hover:z-10 flex flex-col items-center justify-center"
                              style={{
                                backgroundColor: getHeatmapColor(stock.changePercent),
                                width: isMegaCap ? '80px' : isLargeCap ? '60px' : '48px',
                                height: isMegaCap ? '50px' : isLargeCap ? '40px' : '32px',
                              }}
                              data-testid={`heatmap-${ticker}`}
                             target="_blank" rel="noopener noreferrer">
                              <div className={`font-bold text-white ${isMegaCap ? 'text-xs' : 'text-[10px]'}`}>{ticker}</div>
                              <div className={`text-white/90 ${isMegaCap ? 'text-[10px]' : 'text-[8px]'}`}>
                                {formatPercent(stock.changePercent)}
                              </div>
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Sector Performance Mini-bars */}
                <div className="border-t border-gray-800 p-2">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] text-gray-500 uppercase tracking-wide">Sector Performance</div>
                    <span className="text-[9px] text-gray-500" data-testid="sector-bars-source-label"
                      title="Shared MSN sector-ETF snapshot — refreshed every 60s; mirrors the GICS Sector 1D column">
                      {data.sectors_source || 'MSN Finance'}{data.sectors_as_of ? ` · ${new Date(data.sectors_as_of).toLocaleTimeString()}` : ''}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {data.sectors?.map((sector) => (
                      <div key={sector.ticker} className="flex items-center gap-2" data-testid={`sector-${sector.ticker}`}>
                        <div className="w-16 text-[10px] text-gray-400 truncate">{sector.name}</div>
                        <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${sector.changePercent >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                            style={{ 
                              width: `${Math.min(Math.abs(sector.changePercent || 0) * 15, 100)}%`,
                            }}
                          />
                        </div>
                        <div className={`text-[10px] font-medium w-12 text-right ${getChangeClass(sector.changePercent)}`}>
                          {formatPercent(sector.changePercent)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN - News & Info */}
            <div className="col-span-12 lg:col-span-3 space-y-0.5">
              {/* HOT IN CONGRESS THIS WEEK — top 5 PTR ticker swings */}
              <div className="mb-1">
                <HotInCongressCard />
              </div>
              {/* POLITICAL/GEO NEWS - PRIMARY SECTION */}
              <div className="bg-[#111] border border-red-900/50 rounded">
                <div className="bg-gradient-to-r from-red-900/40 to-[#1a1a2e] px-2 py-1.5 border-b border-red-900/50 flex items-center gap-1.5">
                  <Landmark className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-semibold text-white">Political & Geopolitical</span>
                  <button onClick={fetchMarketData} className="text-gray-500 hover:text-red-400 ml-auto p-0.5 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-political" title="Reload">
                    <RefreshCw className="w-3 h-3" />
                  </button>
                </div>
                <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-800/50">
                  {politicalNews.length > 0 ? politicalNews.slice(0, 10).map((item, idx) => (
                    <a 
                      key={idx}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-1.5 hover:bg-red-900/20 transition-colors"
                      data-testid={`political-news-${idx}`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-[9px] text-red-400 shrink-0 pt-0.5 font-medium">
                          {item.source?.substring(0, 8) || 'NEWS'}
                        </span>
                        <p className="text-[11px] text-gray-200 line-clamp-2 leading-tight">{item.title}</p>
                      </div>
                    </a>
                  )) : (
                    <div className="p-3 text-center text-gray-500 text-xs">
                      Loading political news...
                    </div>
                  )}
                </div>
              </div>

              {/* Market Headlines - Market-Moving Events */}
              <div className="bg-[#111] border border-blue-900/50 rounded">
                <div className="bg-gradient-to-r from-blue-900/40 to-[#1a1a2e] px-2 py-1.5 border-b border-blue-900/50 flex items-center gap-1.5">
                  <Newspaper className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-xs font-semibold text-white">Market Headlines</span>
                  <span className="text-[9px] text-blue-400 ml-auto">LIVE</span>
                </div>
                <div className="max-h-[280px] overflow-y-auto divide-y divide-gray-800/50">
                  {marketNews.length > 0 ? marketNews.slice(0, 12).map((item, idx) => (
                    <a 
                      key={idx}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-2 hover:bg-blue-900/20 transition-colors group"
                      data-testid={`market-news-${idx}`}
                    >
                      <p className="text-[11px] text-gray-200 group-hover:text-white leading-tight mb-1 line-clamp-2">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] px-1.5 py-0.5 bg-blue-900/30 text-blue-400 rounded">
                          {item.source?.substring(0, 12) || 'Market'}
                        </span>
                        <span className="text-[9px] text-gray-500">
                          {formatRelativeTime(item.published)}
                        </span>
                        <ExternalLink className="w-2.5 h-2.5 text-gray-600 group-hover:text-blue-400 ml-auto" />
                      </div>
                    </a>
                  )) : news.length > 0 ? news.slice(0, 12).map((item, idx) => (
                    <a 
                      key={idx}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-2 py-2 hover:bg-blue-900/20 transition-colors group"
                      data-testid={`news-${idx}`}
                    >
                      <p className="text-[11px] text-gray-200 group-hover:text-white leading-tight mb-1 line-clamp-2">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] px-1.5 py-0.5 bg-blue-900/30 text-blue-400 rounded">
                          {item.source?.substring(0, 12) || 'Market'}
                        </span>
                        <span className="text-[9px] text-gray-500">
                          {formatRelativeTime(item.published)}
                        </span>
                        <ExternalLink className="w-2.5 h-2.5 text-gray-600 group-hover:text-blue-400 ml-auto" />
                      </div>
                    </a>
                  )) : (
                    <div className="p-4 text-center text-gray-500 text-xs">
                      <RefreshCw className="w-4 h-4 animate-spin mx-auto mb-2" />
                      Loading market news...
                    </div>
                  )}
                </div>
              </div>

              {/* In Focus - Major Stocks */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center gap-1.5">
                  <Flame className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-semibold text-white">In Focus</span>
                </div>
                <div className="p-2 grid grid-cols-2 gap-1">
                  {["NVDA", "TSLA", "AAPL", "MSFT", "META", "GOOGL", "AMZN", "AMD"].map((ticker) => {
                    const stock = getStockData(ticker);
                    if (!stock) return null;
                    return (
                      <a 
                        key={ticker}
                        href={`/stock?ticker=${ticker}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between px-1.5 py-1 rounded hover:bg-gray-800/50"
                      >
                        <span className="text-[11px] text-blue-400 font-medium">{ticker}</span>
                        <span className={`text-[10px] font-medium ${getChangeClass(stock.changePercent)}`}>
                          {formatPercent(stock.changePercent)}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>

              {/* Futures */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center gap-1.5">
                  <Globe className="w-3.5 h-3.5 text-purple-500" />
                  <span className="text-xs font-semibold text-white">Futures</span>
                </div>
                <table className="w-full text-[11px]">
                  <tbody className="divide-y divide-gray-800/50">
                    {[
                      { name: "S&P 500", ticker: "ES", data: data.indices?.find(i => i.name === "S&P 500") },
                      { name: "Nasdaq 100", ticker: "NQ", data: data.indices?.find(i => i.name === "NASDAQ 100") },
                      { name: "Dow", ticker: "YM", data: data.indices?.find(i => i.name === "DOW 30") },
                      { name: "Russell 2000", ticker: "ER2", data: data.indices?.find(i => i.name === "Russell 2000") },
                    ].map((future) => (
                      <tr key={future.ticker} className="hover:bg-gray-800/30">
                        <td className="px-2 py-1 text-gray-400">{future.name}</td>
                        <td className="text-right px-2 py-1 text-gray-300">{formatPrice(future.data?.price)}</td>
                        <td className={`text-right px-2 py-1 font-medium ${getChangeClass(future.data?.changePercent)}`}>
                          {formatPercent(future.data?.changePercent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Sector ETFs */}
              <div className="bg-[#111] border border-gray-800 rounded">
                <div className="bg-[#1a1a2e] px-2 py-1.5 border-b border-gray-800 flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-500" />
                  <span className="text-xs font-semibold text-white">Sector ETFs</span>
                </div>
                <table className="w-full text-[11px]">
                  <tbody className="divide-y divide-gray-800/50">
                    {data.sectors?.slice(0, 8).map((sector) => (
                      <tr key={sector.ticker} className="hover:bg-gray-800/30">
                        <td className="px-2 py-1">
                          <a href={`/stock?ticker=${sector.ticker}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">
                            {sector.ticker}
                          </a>
                        </td>
                        <td className="px-2 py-1 text-gray-500 text-[10px] truncate max-w-[80px]">{sector.name}</td>
                        <td className="text-right px-2 py-1 text-gray-300">{formatPrice(sector.price)}</td>
                        <td className={`text-right px-2 py-1 font-medium ${getChangeClass(sector.changePercent)}`}>
                          {formatPercent(sector.changePercent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* SECTOR & SUB-SECTOR PERFORMANCE - Full Width Section */}
          <div className="px-1 mt-0.5" data-testid="sector-subsector-performance">
            <div className="bg-[#111] border border-gray-800 rounded">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#1a1a2e] to-[#111] px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <PieChart className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-white">GICS Sector & Sub-Sector Performance</span>
                  <span className="text-[10px] text-gray-500 ml-2">All 11 Sectors</span>
                  {sectorPerformanceData?.as_of && (
                    <span className="text-[9px] text-gray-500 ml-2" data-testid="gics-header-source-label"
                      title="1D / 5D / 20D / 1Y from the shared MSN sector-ETF snapshot (refreshed every 60s) — same values as the Sector Performance bars">
                      {sectorPerformanceData.source || 'MSN Finance'} · {new Date(sectorPerformanceData.as_of).toLocaleTimeString()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  {/* Timeframe Toggle */}
                  <div className="flex items-center bg-[#0d0d0d] rounded-lg p-0.5 flex-wrap">
                    {[
                      { key: 'day',  label: '1D' },
                      { key: '5d',   label: '5D' },
                      { key: '10d',  label: '10D' },
                      { key: '20d',  label: '20D' },
                      { key: '50d',  label: '50D' },
                      { key: '100d', label: '100D' },
                      { key: '200d', label: '200D' },
                      { key: '1y',   label: '1Y' },
                      { key: '3y',   label: '3Y' },
                      { key: '5y',   label: '5Y' }
                    ].map(({ key, label }) => (
                      <button
                        key={key}
                        onClick={() => setPerformanceTimeframe(key)}
                        className={`px-2 py-1 text-[10px] font-medium rounded-md transition-all ${
                          performanceTimeframe === key
                            ? 'bg-purple-600 text-white'
                            : 'text-gray-400 hover:text-white'
                        }`}
                        data-testid={`timeframe-${key}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Refresh */}
                  <button
                    onClick={fetchSectorPerformance}
                    disabled={sectorPerformanceLoading}
                    className="text-gray-400 hover:text-white p-1 rounded hover:bg-gray-800"
                    data-testid="refresh-sector-performance"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${sectorPerformanceLoading ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-2">
                {sectorPerformanceLoading && !sectorPerformanceData ? (
                  <div className="flex items-center justify-center py-12">
                    <RefreshCw className="w-6 h-6 animate-spin text-purple-500" />
                    <span className="ml-2 text-gray-400 text-sm">Loading sector data...</span>
                  </div>
                ) : sectorPerformanceData?.sectors?.length > 0 ? (
                  (() => {
                    // Sort sectors (and each sector's sub-sectors) by the
                    // currently-selected timeframe descending so the strongest
                    // groups are always at the top of the table.
                    const sortedSectors = [...sectorPerformanceData.sectors]
                      .map(s => ({
                        ...s,
                        sub_sectors: Array.isArray(s.sub_sectors)
                          ? [...s.sub_sectors].sort((a, b) => {
                              const av = getPerformanceValue(a);
                              const bv = getPerformanceValue(b);
                              if (av == null && bv == null) return 0;
                              if (av == null) return 1;
                              if (bv == null) return -1;
                              return bv - av;
                            })
                          : s.sub_sectors,
                      }))
                      .sort((a, b) => {
                        const av = getPerformanceValue(a);
                        const bv = getPerformanceValue(b);
                        if (av == null && bv == null) return 0;
                        if (av == null) return 1;
                        if (bv == null) return -1;
                        return bv - av;
                      });
                    const tfLabelMap = {
                      day: '1D', '5d': '5D', '10d': '10D', '20d': '20D',
                      '50d': '50D', '100d': '100D', '200d': '200D',
                      '1y': '1Y', '3y': '3Y', '5y': '5Y',
                    };
                    const activeTfLabel = tfLabelMap[performanceTimeframe] || '1D';
                    // Configuration for the 10 return columns rendered in the
                    // header + every sector / sub-sector row.  Order matches
                    // the timeframe-toggle pills above.
                    const RETURN_COLS = [
                      { label: '1D',   field: 'day_return',                   active: performanceTimeframe === 'day'  },
                      { label: '5D',   field: 'ret_5d',   legacy: 'week_return',        active: performanceTimeframe === '5d'   },
                      { label: '10D',  field: 'ret_10d',                       active: performanceTimeframe === '10d'  },
                      { label: '20D',  field: 'ret_20d',  legacy: 'month_return',       active: performanceTimeframe === '20d'  },
                      { label: '50D',  field: 'ret_50d',  legacy: 'three_month_return', active: performanceTimeframe === '50d'  },
                      { label: '100D', field: 'ret_100d', legacy: 'six_month_return',   active: performanceTimeframe === '100d' },
                      { label: '200D', field: 'ret_200d',                      active: performanceTimeframe === '200d' },
                      { label: '1Y',   field: 'ret_1y',   legacy: 'year_return',        active: performanceTimeframe === '1y'   },
                      { label: '3Y',   field: 'ret_3y',                        active: performanceTimeframe === '3y'   },
                      { label: '5Y',   field: 'ret_5y',                        active: performanceTimeframe === '5y'   },
                    ];
                    const valOf = (item, c) => {
                      const v = item[c.field];
                      if (v != null) return v;
                      if (c.legacy && item[c.legacy] != null) return item[c.legacy];
                      return 0;
                    };
                    return (
                  <div className="space-y-1" data-testid="sector-rows-sorted">
                   {/* Horizontal scroll on small screens — like US Index Returns */}
                   <div className="overflow-x-auto">
                    <div className="min-w-[980px]">
                    {/* Table Header */}
                    <div className="flex items-center px-3 py-2 text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800">
                      <div className="w-[22%] min-w-[180px]">Sector / Sub-Sector</div>
                      <div className="w-[6%] text-center">ETF</div>
                      {RETURN_COLS.map((c) => (
                        <div
                          key={c.label}
                          className={`w-[7.2%] text-right flex items-center justify-end gap-1 ${c.active ? 'text-purple-300 font-bold' : ''}`}
                        >
                          {c.active && <ChevronDown className="w-3 h-3" />}
                          {c.label}
                        </div>
                      ))}
                    </div>

                    {/* Sector Rows */}
                    {sortedSectors.map((sector) => (
                      <div key={sector.name} className="group">
                        {/* Main Sector Row */}
                        <div
                          className="flex items-center px-3 py-2 rounded-lg cursor-pointer transition-all hover:bg-gray-800/50"
                          style={{
                            borderLeft: `3px solid ${sector.color}`,
                            backgroundColor: expandedSectors[sector.name] ? 'rgba(31, 41, 55, 0.5)' : 'transparent'
                          }}
                          onClick={() => toggleSectorExpanded(sector.name)}
                          data-testid={`sector-row-${sector.etf}`}
                        >
                          <div className="w-[22%] min-w-[180px] flex items-center gap-2">
                            <button className="text-gray-400 hover:text-white">
                              {expandedSectors[sector.name] ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </button>
                            <span className="text-xs font-medium text-white truncate">{sector.name}</span>
                            <span className="text-[9px] text-gray-500 hidden lg:inline">
                              ({sector.sub_sectors?.length || 0})
                            </span>
                          </div>
                          <div className="w-[6%] text-center">
                            <a
                              href={`/stock?ticker=${sector.etf}`}
                              className="text-[11px] text-blue-400 hover:underline font-medium"
                              target="_blank" rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {sector.etf}
                            </a>
                          </div>
                          {RETURN_COLS.map((c) => {
                            const v = valOf(sector, c);
                            return (
                              <div
                                key={c.label}
                                className={`w-[7.2%] text-right text-xs font-medium ${v >= 0 ? 'text-green-400' : 'text-red-400'}`}
                              >
                                {v >= 0 ? '+' : ''}{v.toFixed(2)}%
                              </div>
                            );
                          })}
                        </div>

                        {/* Sub-Sectors (Collapsible) */}
                        {expandedSectors[sector.name] && sector.sub_sectors?.length > 0 && (
                          <div className="ml-6 border-l border-gray-700/50 pl-2 py-1 animate-in slide-in-from-top-1 duration-200">
                            {sector.sub_sectors.map((sub, idx) => (
                              <div
                                key={sub.name}
                                className="flex items-center px-3 py-1.5 rounded hover:bg-gray-800/30 transition-colors"
                                data-testid={`subsector-row-${idx}`}
                              >
                                <div className="w-[22%] min-w-[180px] flex items-center gap-2">
                                  <Layers className="w-3 h-3 text-gray-600" />
                                  <span className="text-[11px] text-gray-300 truncate">{sub.name}</span>
                                </div>
                                <div className="w-[6%] text-center">
                                  <a
                                    href={`/stock?ticker=${sub.etf}`}
                                    className="text-[10px] text-blue-400/70 hover:underline"
                                   target="_blank" rel="noopener noreferrer">
                                    {sub.etf}
                                  </a>
                                </div>
                                {RETURN_COLS.map((c) => {
                                  const v = valOf(sub, c);
                                  return (
                                    <div
                                      key={c.label}
                                      className={`w-[7.2%] text-right text-[11px] ${v >= 0 ? 'text-green-400/80' : 'text-red-400/80'}`}
                                    >
                                      {v >= 0 ? '+' : ''}{v.toFixed(2)}%
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                   </div>

                    {/* Performance Summary Bar */}
                    <div className="mt-4 pt-3 border-t border-gray-800">
                      <div className="flex items-center justify-between px-3">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">
                          Performance Heatmap ({
                            performanceTimeframe === 'day'  ? '1 Day' :
                            performanceTimeframe === '5d'   ? '5 Trading Days' :
                            performanceTimeframe === '10d'  ? '10 Trading Days' :
                            performanceTimeframe === '20d'  ? '20 Trading Days' :
                            performanceTimeframe === '50d'  ? '50 Trading Days' :
                            performanceTimeframe === '100d' ? '100 Trading Days' :
                            performanceTimeframe === '200d' ? '200 Trading Days' :
                            performanceTimeframe === '3y'   ? '3 Years' :
                            performanceTimeframe === '5y'   ? '5 Years' : '1 Year'
                          })
                        </div>
                        <div className="flex items-center gap-1 text-[9px]">
                          <span className="w-3 h-3 rounded bg-red-600"></span>
                          <span className="text-gray-500 mr-2">-3%+</span>
                          <span className="w-3 h-3 rounded bg-red-400"></span>
                          <span className="text-gray-500 mr-2">-1%</span>
                          <span className="w-3 h-3 rounded bg-gray-600"></span>
                          <span className="text-gray-500 mr-2">0%</span>
                          <span className="w-3 h-3 rounded bg-green-400"></span>
                          <span className="text-gray-500 mr-2">+1%</span>
                          <span className="w-3 h-3 rounded bg-green-600"></span>
                          <span className="text-gray-500">+3%+</span>
                        </div>
                      </div>
                      <div className="grid grid-cols-11 gap-1 mt-2 px-3">
                        {sortedSectors.map((sector) => {
                          const value = getPerformanceValue(sector);
                          let bgColor = 'bg-gray-600';
                          if (value >= 3) bgColor = 'bg-green-600';
                          else if (value >= 1) bgColor = 'bg-green-500';
                          else if (value >= 0.5) bgColor = 'bg-green-400';
                          else if (value > 0) bgColor = 'bg-green-400/60';
                          else if (value > -0.5) bgColor = 'bg-red-400/60';
                          else if (value > -1) bgColor = 'bg-red-400';
                          else if (value > -3) bgColor = 'bg-red-500';
                          else bgColor = 'bg-red-600';

                          return (
                            <div
                              key={sector.name}
                              className={`${bgColor} rounded p-2 text-center cursor-pointer hover:opacity-80 transition-opacity`}
                              onClick={() => toggleSectorExpanded(sector.name)}
                              title={`${sector.name}: ${value >= 0 ? '+' : ''}${value.toFixed(2)}%`}
                              data-testid={`heatmap-sector-${sector.etf}`}
                            >
                              <div className="text-[9px] font-bold text-white truncate">{sector.etf}</div>
                              <div className="text-[8px] text-white/80">
                                {value >= 0 ? '+' : ''}{value.toFixed(1)}%
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Leveraged Bull & Bear ETF sections */}
                    {(() => {
                      const sortLevRows = (rows) => [...rows].sort((a, b) => {
                        const av = getPerformanceValue(a);
                        const bv = getPerformanceValue(b);
                        if (av == null && bv == null) return 0;
                        if (av == null) return 1;
                        if (bv == null) return -1;
                        return bv - av;
                      });
                      const renderLevRow = (lev, badgeCls, isSub) => (
                        <div
                          key={lev.etf + lev.sector}
                          className="flex items-center px-3 py-2 rounded-lg transition-all hover:bg-gray-800/50"
                          style={{ borderLeft: `3px solid ${lev.color}` }}
                          data-testid={`leveraged-row-${lev.etf}`}
                        >
                          <div className={`w-[22%] min-w-[180px] flex items-center gap-2 ${isSub ? 'pl-9' : 'pl-6'}`}>
                            {isSub && <Layers className="w-3 h-3 text-gray-600 shrink-0" />}
                            <span className={`${isSub ? 'text-[11px] text-gray-300' : 'text-xs text-white'} font-medium truncate`}>{lev.sector}</span>
                            <span className={`text-[8px] font-bold rounded px-1 py-px border ${badgeCls}`}>{lev.leverage}</span>
                            {lev.note && <span className="text-[8px] text-gray-500 hidden lg:inline">({lev.note})</span>}
                          </div>
                          <div className="w-[6%] text-center">
                            <a
                              href={`/stock?ticker=${lev.etf}`}
                              className="text-[11px] text-blue-400 hover:underline font-medium"
                              target="_blank" rel="noopener noreferrer"
                            >
                              {lev.etf}
                            </a>
                          </div>
                          {RETURN_COLS.map((c) => {
                            const v = valOf(lev, c);
                            return (
                              <div
                                key={c.label}
                                className={`w-[7.2%] text-right text-xs font-medium ${v >= 0 ? 'text-green-400' : 'text-red-400'}`}
                              >
                                {v >= 0 ? '+' : ''}{v.toFixed(2)}%
                              </div>
                            );
                          })}
                        </div>
                      );
                      const renderLevSection = (rows, { title, subtitle, testid, Icon, iconCls, activeCls, badgeCls }) => {
                        if (!rows?.length) return null;
                        const sectorRows = sortLevRows(rows.filter(r => r.scope !== 'sub-sector'));
                        const subRows = sortLevRows(rows.filter(r => r.scope === 'sub-sector'));
                        return (
                          <div className="mt-4 pt-3 border-t border-gray-800" data-testid={testid}>
                            <div className="flex items-center gap-2 px-3 mb-1">
                              <Icon className={`w-3.5 h-3.5 ${iconCls}`} />
                              <span className="text-xs font-semibold text-white">{title}</span>
                              <span className="text-[9px] text-gray-500">{subtitle}</span>
                            </div>
                            {/* Horizontal scroll on small screens — like US Index Returns */}
                            <div className="overflow-x-auto">
                             <div className="min-w-[980px]">
                            <div className="flex items-center px-3 py-2 text-[9px] text-gray-500 uppercase tracking-wider border-b border-gray-800">
                              <div className="w-[22%] min-w-[180px]">Sector / Sub-Sector</div>
                              <div className="w-[6%] text-center">ETF</div>
                              {RETURN_COLS.map((c) => (
                                <div
                                  key={c.label}
                                  className={`w-[7.2%] text-right flex items-center justify-end gap-1 ${c.active ? `${activeCls} font-bold` : ''}`}
                                >
                                  {c.active && <ChevronDown className="w-3 h-3" />}
                                  {c.label}
                                </div>
                              ))}
                            </div>
                            {sectorRows.map((lev) => renderLevRow(lev, badgeCls, false))}
                            {subRows.length > 0 && (
                              <div className="flex items-center gap-2 px-3 pt-2 pb-1">
                                <Layers className="w-3 h-3 text-gray-600" />
                                <span className="text-[9px] text-gray-500 uppercase tracking-wider">Sub-Sectors</span>
                              </div>
                            )}
                            {subRows.map((lev) => renderLevRow(lev, badgeCls, true))}
                             </div>
                            </div>
                          </div>
                        );
                      };
                      return (
                        <>
                          {renderLevSection(sectorPerformanceData?.leveraged, {
                            title: 'Triple Leveraged Bull ETFs',
                            subtitle: 'Daily bull ETFs — returns compound daily, not for long holds',
                            testid: 'leveraged-sector-section',
                            Icon: Zap,
                            iconCls: 'text-amber-400',
                            activeCls: 'text-amber-300',
                            badgeCls: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
                          })}
                          {renderLevSection(sectorPerformanceData?.leveraged_bear, {
                            title: 'Triple Leveraged Bear ETFs',
                            subtitle: 'Inverse daily ETFs — profit when the sector falls; decay on long holds',
                            testid: 'leveraged-bear-section',
                            Icon: TrendingDown,
                            iconCls: 'text-red-400',
                            activeCls: 'text-red-300',
                            badgeCls: 'text-red-400 bg-red-500/10 border-red-500/30',
                          })}
                        </>
                      );
                    })()}
                  </div>
                  );
                  })()
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <PieChart className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No sector performance data available</p>
                  </div>
                )}
                <OptionsScannerSection />
              </div>

              {/* Footer Info */}
              <div className="px-4 py-2 bg-[#0d0d0d] border-t border-gray-800 text-[9px] text-gray-600 flex items-center justify-between">
                <span>Data: GICS Classification • Sector ETFs • {sectorPerformanceData?.source || 'MSN Finance'} (1D/5D/20D/1Y) • Yahoo history (3Y/5Y)</span>
                <span data-testid="gics-source-label">
                  {sectorPerformanceData?.source || 'MSN Finance'} · {sectorPerformanceData?.as_of ? new Date(sectorPerformanceData.as_of).toLocaleTimeString() : 'Loading...'}
                </span>
              </div>
            </div>
          </div>

          {/* NEW SECTIONS - Economic Data, Trump Posts, US News */}
          <div className="grid grid-cols-12 gap-0.5 p-1 mt-0.5">
            
            {/* BLS Economic Indicators */}
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-[#111] border border-blue-900/50 rounded h-full">
                <div className="bg-gradient-to-r from-blue-900/40 to-[#1a1a2e] px-3 py-2 border-b border-blue-900/50 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-400" />
                  <span className="text-xs font-semibold text-white">BLS Economic Data</span>
                  <span className="text-[9px] text-blue-400 ml-auto">Weekly Updates</span>
                  <button onClick={fetchEconomicIndicators} className="text-gray-500 hover:text-blue-400 p-1 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-bls" title="Reload">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
                  {/* Weekly News Releases */}
                  {economicIndicators?.weekly_news?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-[10px] text-blue-400 font-semibold mb-2 uppercase tracking-wide">Latest News Releases</div>
                      {economicIndicators.weekly_news.slice(0, 5).map((news, idx) => (
                        <a 
                          key={idx}
                          href={news.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block bg-[#0d0d0d] rounded p-2 mb-1.5 hover:bg-gray-800/50 transition-colors"
                          data-testid={`bls-news-${idx}`}
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-[9px] text-blue-400 whitespace-nowrap font-medium">{news.date}</span>
                            <span className="text-[10px] text-gray-200 leading-tight">{news.headline}</span>
                          </div>
                        </a>
                      ))}
                      <a 
                        href="https://www.bls.gov/bls/newsrels.htm"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[9px] text-blue-400 hover:text-blue-300 flex items-center gap-1 mt-1"
                      >
                        View All Releases <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </div>
                  )}
                  
                  {/* Divider */}
                  {economicIndicators?.weekly_news?.length > 0 && economicIndicators?.indicators?.length > 0 && (
                    <div className="border-t border-gray-800 my-2 pt-2">
                      <div className="text-[10px] text-blue-400 font-semibold mb-2 uppercase tracking-wide">Key Indicators</div>
                    </div>
                  )}
                  
                  {/* Economic Indicators */}
                  {economicIndicators?.indicators?.length > 0 ? (
                    economicIndicators.indicators.map((indicator, idx) => (
                      <div key={idx} className="bg-[#0d0d0d] rounded p-2 hover:bg-gray-800/50 transition-colors" data-testid={`economic-${indicator.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="text-xs font-medium text-white mb-0.5">{indicator.name}</div>
                            <div className="text-lg font-bold text-blue-400">
                              {indicator.value}
                              {indicator.preliminary && <span className="text-[9px] text-yellow-400 ml-1">(p)</span>}
                            </div>
                            <div className="text-[10px] text-gray-500">{indicator.period}</div>
                          </div>
                          <div className="flex flex-col gap-1">
                            <a 
                              href={indicator.news_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[9px] text-blue-400 hover:text-blue-300 flex items-center gap-0.5"
                            >
                              News Release <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                            <a 
                              href={indicator.historical_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[9px] text-gray-400 hover:text-gray-300 flex items-center gap-0.5"
                            >
                              Historical Data <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="space-y-2" data-testid="economic-skeleton">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <div key={idx} className="bg-[#0d0d0d] rounded p-2 animate-pulse">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="h-3 w-32 bg-gray-700/50 rounded mb-1.5" />
                              <div className="h-5 w-16 bg-blue-900/30 rounded mb-1" />
                              <div className="h-2 w-20 bg-gray-700/40 rounded" />
                            </div>
                            <div className="flex flex-col gap-1">
                              <div className="h-2.5 w-16 bg-blue-900/30 rounded" />
                              <div className="h-2.5 w-18 bg-gray-700/40 rounded" />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-3 py-1.5 bg-[#0a0a0a] border-t border-gray-800 text-[9px] text-gray-500">
                  Source: <a href="https://www.bls.gov" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300">Bureau of Labor Statistics</a>
                </div>
              </div>
            </div>

            {/* Trump Truth Social Posts */}
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-[#111] border border-red-900/50 rounded h-full">
                <div className="bg-gradient-to-r from-red-900/40 to-[#1a1a2e] px-3 py-2 border-b border-red-900/50 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-red-400" />
                  <span className="text-xs font-semibold text-white">Trump Truth Social</span>
                  <span className="text-[9px] text-red-400 ml-auto">
                    @realDonaldTrump
                    {trumpPosts?.direct_trump_count > 0 && (
                      <span className="ml-1 px-1 py-0.5 bg-yellow-500/20 text-yellow-400 rounded">
                        {trumpPosts.direct_trump_count} Direct
                      </span>
                    )}
                  </span>
                  <button onClick={fetchTrumpPosts} className="text-gray-500 hover:text-red-400 p-1 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-trump" title="Reload">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="p-2 space-y-2 max-h-[350px] overflow-y-auto">
                  {trumpPosts?.posts?.length > 0 ? (
                    trumpPosts.posts.map((post, idx) => (
                      <div 
                        key={idx} 
                        className={`rounded p-2.5 transition-colors ${
                          post.is_direct_trump 
                            ? 'bg-yellow-900/20 border border-yellow-500/50 hover:bg-yellow-900/30' 
                            : 'bg-[#0d0d0d] hover:bg-gray-800/50'
                        }`}
                        data-testid={`trump-post-${idx}`}
                      >
                        {post.is_direct_trump && (
                          <div className="flex items-center gap-1 mb-1.5">
                            <span className="text-[9px] px-1.5 py-0.5 bg-yellow-500/30 text-yellow-300 rounded font-semibold">
                              @realDonaldTrump
                            </span>
                            <span className="text-[9px] text-yellow-400">DIRECT POST</span>
                          </div>
                        )}
                        <p className={`text-[11px] leading-relaxed mb-1.5 ${
                          post.is_direct_trump ? 'text-yellow-100 font-medium' : 'text-gray-200'
                        }`}>
                          {post.content}
                        </p>
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] text-gray-500">
                            {post.timestamp ? formatRelativeTime(post.timestamp) : ''}
                          </span>
                          {post.link && (
                            <a 
                              href={post.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[9px] text-red-400 hover:text-red-300 flex items-center gap-0.5"
                            >
                              Source <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                            post.is_direct_trump 
                              ? 'bg-yellow-500/30 text-yellow-300' 
                              : 'bg-red-900/30 text-red-400'
                          }`}>
                            {post.source}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="space-y-2" data-testid="trump-posts-skeleton">
                      {Array.from({ length: 4 }).map((_, idx) => (
                        <div key={idx} className="bg-[#0d0d0d] rounded p-2.5 animate-pulse">
                          <div className="h-3 w-full bg-gray-700/40 rounded mb-1.5" />
                          <div className="h-3 w-4/5 bg-gray-700/40 rounded mb-1.5" />
                          <div className="h-3 w-2/3 bg-gray-700/40 rounded mb-2" />
                          <div className="flex items-center justify-between">
                            <div className="h-2 w-12 bg-gray-700/30 rounded" />
                            <div className="h-3 w-16 bg-red-900/30 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-3 py-1.5 bg-[#0a0a0a] border-t border-gray-800 text-[9px] text-gray-500">
                  Platform: Truth Social • {trumpPosts?.note || 'Sorted by date (most recent first)'}
                </div>
              </div>
            </div>

            {/* US Financial News */}
            <div className="col-span-12 lg:col-span-4">
              <div className="bg-[#111] border border-emerald-900/50 rounded h-full">
                <div className="bg-gradient-to-r from-emerald-900/40 to-[#1a1a2e] px-3 py-2 border-b border-emerald-900/50 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-emerald-400" />
                  <span className="text-xs font-semibold text-white">US Financial News</span>
                  <span className="text-[9px] text-emerald-400 ml-auto">{usFinancialNews?.source_count || 0} Sources</span>
                  <button onClick={fetchUsFinancialNews} className="text-gray-500 hover:text-emerald-400 p-1 rounded hover:bg-gray-800 transition-colors" data-testid="refresh-us-news" title="Reload">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="divide-y divide-gray-800/50 max-h-[350px] overflow-y-auto">
                  {usFinancialNews?.all_news?.length > 0 ? (
                    usFinancialNews.all_news.slice(0, 12).map((item, idx) => (
                      <a
                        key={idx}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-3 py-2 hover:bg-emerald-900/20 transition-colors group"
                        data-testid={`us-news-${idx}`}
                      >
                        <p className="text-[11px] text-gray-200 group-hover:text-white leading-tight mb-1 line-clamp-2">
                          {item.title}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] px-1.5 py-0.5 bg-emerald-900/30 text-emerald-400 rounded">
                            {item.source?.substring(0, 15) || 'News'}
                          </span>
                          <span className="text-[9px] text-gray-500">
                            {formatRelativeTime(item.published)}
                          </span>
                          <ExternalLink className="w-2.5 h-2.5 text-gray-600 group-hover:text-emerald-400 ml-auto" />
                        </div>
                      </a>
                    ))
                  ) : (
                    <div className="divide-y divide-gray-800/30" data-testid="us-news-skeleton">
                      {Array.from({ length: 6 }).map((_, idx) => (
                        <div key={idx} className="px-3 py-2 animate-pulse">
                          <div className="h-3 w-full bg-gray-700/40 rounded mb-1" />
                          <div className="h-3 w-3/4 bg-gray-700/40 rounded mb-2" />
                          <div className="flex items-center gap-2">
                            <div className="h-3 w-14 bg-emerald-900/30 rounded" />
                            <div className="h-2 w-10 bg-gray-700/30 rounded" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="px-3 py-1.5 bg-[#0a0a0a] border-t border-gray-800 text-[9px] text-gray-500">
                  Sources: CNBC • WSJ • Bloomberg • MarketWatch • Yahoo Finance • Fox Business • Barron's
                </div>
              </div>
            </div>
          </div>

          {/* Earnings Calendar - Full Width Section */}
          <div className="p-1 mt-0.5">
            <div className="bg-[#111] border border-amber-900/50 rounded">
              <div className="bg-gradient-to-r from-amber-900/40 to-[#1a1a2e] px-4 py-2 border-b border-amber-900/50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CalendarDays className="w-4 h-4 text-amber-400" />
                  <span className="text-sm font-semibold text-white">Earnings Calendar</span>
                  <span className="text-[10px] text-amber-400 ml-2">
                    {earningsCalendar?.total_count || 0} upcoming reports
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEarningsView('this_week')}
                    className={`px-3 py-1 text-[10px] rounded transition-colors ${
                      earningsView === 'this_week' 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    This Week
                  </button>
                  <button
                    onClick={() => setEarningsView('next_week')}
                    className={`px-3 py-1 text-[10px] rounded transition-colors ${
                      earningsView === 'next_week' 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    Next Week
                  </button>
                  <button
                    onClick={() => setEarningsView('all')}
                    className={`px-3 py-1 text-[10px] rounded transition-colors ${
                      earningsView === 'all' 
                        ? 'bg-amber-600 text-white' 
                        : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                    }`}
                  >
                    All (30 Days)
                  </button>
                  <button onClick={fetchEarningsCalendar} className="text-gray-500 hover:text-amber-400 p-1 rounded hover:bg-gray-800 transition-colors ml-1" data-testid="refresh-earnings" title="Reload">
                    <RefreshCw className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              
              <div className="p-3">
                {earningsCalendar ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="earnings-table">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-800">
                          <th className="pb-2 pl-2 font-medium">Date</th>
                          <th className="pb-2 font-medium">Ticker</th>
                          <th className="pb-2 font-medium">Company</th>
                          <th className="pb-2 font-medium text-center">Time</th>
                          <th className="pb-2 font-medium text-right">EPS Est.</th>
                          <th className="pb-2 font-medium text-right pr-2">Market Cap</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {(earningsView === 'this_week' 
                          ? earningsCalendar.this_week 
                          : earningsView === 'next_week' 
                            ? earningsCalendar.next_week 
                            : earningsCalendar.all_earnings
                        )?.length > 0 ? (
                          (earningsView === 'this_week' 
                            ? earningsCalendar.this_week 
                            : earningsView === 'next_week' 
                              ? earningsCalendar.next_week 
                              : earningsCalendar.all_earnings
                          ).map((earning, idx) => (
                            <tr 
                              key={idx} 
                              className="hover:bg-amber-900/10 transition-colors"
                              data-testid={`earnings-row-${idx}`}
                            >
                              <td className="py-2 pl-2">
                                <div className="flex items-center gap-1.5">
                                  <Calendar className="w-3 h-3 text-amber-400" />
                                  <span className="text-gray-300 font-medium">
                                    {new Date(earning.date).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      weekday: 'short'
                                    })}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2">
                                <a 
                                  href={`/stock?ticker=${earning.ticker}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="font-bold text-amber-400 hover:text-amber-300 transition-colors"
                                >
                                  {earning.ticker}
                                </a>
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-1.5">
                                  <Building2 className="w-3 h-3 text-gray-600" />
                                  <span className="text-gray-400 truncate max-w-[200px]">
                                    {earning.company || earning.ticker}
                                  </span>
                                </div>
                              </td>
                              <td className="py-2 text-center">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                                  earning.time === 'BMO' 
                                    ? 'bg-blue-900/50 text-blue-300' 
                                    : earning.time === 'AMC' 
                                      ? 'bg-purple-900/50 text-purple-300'
                                      : 'bg-gray-800 text-gray-400'
                                }`}>
                                  {earning.time === 'BMO' ? '🌅 Before Open' : 
                                   earning.time === 'AMC' ? '🌙 After Close' : 
                                   'TBD'}
                                </span>
                              </td>
                              <td className="py-2 text-right">
                                <span className={`font-medium ${
                                  earning.eps_estimate && earning.eps_estimate !== '-' 
                                    ? 'text-green-400' 
                                    : 'text-gray-500'
                                }`}>
                                  {earning.eps_estimate || '-'}
                                </span>
                              </td>
                              <td className="py-2 text-right pr-2">
                                <span className="text-gray-400">
                                  {earning.market_cap || '-'}
                                </span>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="6" className="py-8 text-center text-gray-500">
                              No earnings scheduled for this period
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="overflow-x-auto" data-testid="earnings-skeleton">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-500 border-b border-gray-800">
                          <th className="pb-2 pl-2 font-medium">Date</th>
                          <th className="pb-2 font-medium">Ticker</th>
                          <th className="pb-2 font-medium">Company</th>
                          <th className="pb-2 font-medium text-center">Time</th>
                          <th className="pb-2 font-medium text-right">EPS Est.</th>
                          <th className="pb-2 font-medium text-right pr-2">Market Cap</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-800/50">
                        {Array.from({ length: 8 }).map((_, idx) => (
                          <tr key={idx} className="animate-pulse">
                            <td className="py-2 pl-2">
                              <div className="h-3 w-16 bg-gray-700/40 rounded" />
                            </td>
                            <td className="py-2">
                              <div className="h-3 w-10 bg-amber-900/30 rounded" />
                            </td>
                            <td className="py-2">
                              <div className="h-3 w-28 bg-gray-700/40 rounded" />
                            </td>
                            <td className="py-2 text-center">
                              <div className="h-4 w-20 bg-gray-700/30 rounded mx-auto" />
                            </td>
                            <td className="py-2 text-right">
                              <div className="h-3 w-10 bg-gray-700/40 rounded ml-auto" />
                            </td>
                            <td className="py-2 text-right pr-2">
                              <div className="h-3 w-12 bg-gray-700/40 rounded ml-auto" />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              
              <div className="px-4 py-1.5 bg-[#0a0a0a] border-t border-gray-800 text-[9px] text-gray-500 flex items-center justify-between">
                <span>
                  BMO = Before Market Open • AMC = After Market Close • EPS Est. = Earnings Per Share Estimate
                </span>
                <span>
                  Data: Yahoo Finance • Updated: {earningsCalendar?.last_updated ? new Date(earningsCalendar.last_updated).toLocaleTimeString() : 'Loading...'}
                </span>
              </div>
            </div>
          </div>
        </main>
      ) : (
        <div className="text-center py-16">
          <p className="text-gray-500 mb-4">Market data unavailable</p>
          <button
            onClick={fetchMarketData}
            className="flex items-center gap-2 mx-auto px-4 py-2 text-sm font-medium text-blue-400 bg-blue-900/30 hover:bg-blue-800/50 rounded-lg border border-blue-800/50 transition-colors"
            data-testid="retry-market-data"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-[#0d0d0d] border-t border-gray-800 py-3 mt-4">
        <div className="max-w-[1920px] mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] text-gray-600">
            <div>
              Data: Yahoo Finance • NASDAQ • Google Finance | Updated: {lastUpdate?.toLocaleString() || 'Loading...'}
            </div>
            <div className="flex items-center gap-4">
              <a href="/" className="hover:text-gray-400" target="_blank" rel="noopener noreferrer">Market Overview</a>
              <a href="/stock" className="hover:text-gray-400" target="_blank" rel="noopener noreferrer">Stock Analysis</a>
              <a href="/scanner" className="hover:text-gray-400" target="_blank" rel="noopener noreferrer">Screener</a>
              <a href="/portfolio" className="hover:text-gray-400" target="_blank" rel="noopener noreferrer">Portfolio</a>
              <a href="/options-generator" className="hover:text-gray-400" target="_blank" rel="noopener noreferrer">Options</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketOverviewPage;
